import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FaBoxOpen,
  FaCalendarCheck,
  FaCalendarTimes,
  FaCalendarAlt,
  FaClock,
  FaBell,
  FaBarcode,
  FaBuilding,
  FaChartLine,
  FaCheckCircle,
  FaChevronDown,
  FaClipboardList,
  FaCogs,
  FaDownload,
  FaDollarSign,
  FaEdit,
  FaExclamationTriangle,
  FaFileImport,
  FaFileInvoiceDollar,
  FaLayerGroup,
  FaIndustry,
  FaPaperclip,
  FaMapMarkedAlt,
  FaSearch,
  FaRobot,
  FaRedoAlt,
  FaPlus,
  FaShieldAlt,
  FaStickyNote,
  FaTag,
  FaTools,
  FaTrash,
  FaUser,
  FaUsers,
  FaUserShield,
  FaTimes,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import Chart from 'chart.js/auto';
import { read as readXLSX, utils as XLSXUtils, writeFile as writeXLSXFile } from '@redoper1/xlsx-js-style';
import AssetService from '../services/AssetService';
import AIInsightsPanel from '../components/AIInsightsPanel';
import api from '../api';
import Modal from '../components/Modal';
import { useAuth } from '../AuthContext';
import {
  ASSET_MANAGE_PERMISSION,
  ASSET_VIEW_PERMISSION,
  hasPermission,
} from '../utils/permissions';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
  paginateTableRows,
} from '../utils/tablePagination';

const STATUS_OPTIONS = [
  { value: 'OPERATING', label: 'Em operação' },
  { value: 'MAINTENANCE', label: 'Em manutenção' },
  { value: 'STOCK', label: 'Em estoque' },
  { value: 'DISPOSED', label: 'Baixado' },
];

const ASSET_STATUS_META = {
  OPERATING: {
    icon: FaCheckCircle,
    activeClass: 'border-emerald-300 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-100',
    iconClass: 'bg-emerald-100 text-emerald-600',
  },
  MAINTENANCE: {
    icon: FaTools,
    activeClass: 'border-amber-300 bg-amber-50 text-amber-800 ring-2 ring-amber-100',
    iconClass: 'bg-amber-100 text-amber-600',
  },
  STOCK: {
    icon: FaBoxOpen,
    activeClass: 'border-sky-300 bg-sky-50 text-sky-800 ring-2 ring-sky-100',
    iconClass: 'bg-sky-100 text-sky-600',
  },
  DISPOSED: {
    icon: FaTrash,
    activeClass: 'border-rose-300 bg-rose-50 text-rose-800 ring-2 ring-rose-100',
    iconClass: 'bg-rose-100 text-rose-600',
  },
};

const IMPORT_REQUIRED_FIELDS = ['name', 'status', 'category', 'location'];

const IMPORT_COLUMNS = [
  { key: 'name', label: 'Nome do ativo', required: true, instructions: 'Nome principal do ativo.' },
  { key: 'tag', label: 'Etiqueta', required: false, instructions: 'Código interno/patrimônio.' },
  { key: 'serialNumber', label: 'Número de série', required: false, instructions: 'Número de série do fabricante.' },
  { key: 'brand', label: 'Marca', required: false, instructions: 'Marca do item (campo opcional).' },
  { key: 'status', label: 'Status', required: true, instructions: 'Em operação, Em manutenção, Em estoque ou Baixado.' },
  { key: 'category', label: 'Categoria', required: true, instructions: 'Nome da categoria já cadastrada.' },
  { key: 'location', label: 'Localização', required: true, instructions: 'Nome do local já cadastrado.' },
  { key: 'acquisitionDate', label: 'Data de aquisição', required: false, instructions: 'Informe AAAA, MM/AAAA ou data completa.' },
  { key: 'acquisitionCost', label: 'Valor de aquisição', required: false, instructions: 'Valor numérico (ex: 14500.90).' },
  { key: 'depreciationRate', label: 'Taxa de depreciação (%)', required: false, instructions: 'Percentual anual.' },
  { key: 'usefulLifeMonths', label: 'Vida útil (meses)', required: false, instructions: 'Quantidade de meses.' },
  { key: 'warrantyEnd', label: 'Garantia até', required: false, instructions: 'Data no formato AAAA-MM-DD.' },
  { key: 'custodian', label: 'Responsável', required: false, instructions: 'Pessoa/área responsável.' },
  { key: 'supplier', label: 'Fornecedor', required: false, instructions: 'Nome do fornecedor.' },
  { key: 'entryOrigin', label: 'Origem da entrada', required: false, instructions: 'Origem da entrada do ativo (ex: compra, doação, transferência).' },
  { key: 'invoiceNumber', label: 'N° da Nota Fiscal', required: false, instructions: 'Número da nota fiscal do ativo.' },
  { key: 'disposalDate', label: 'Data de baixa patrimonial', required: false, instructions: 'Data da baixa patrimonial. Usada quando o status for Baixado.' },
  { key: 'disposalReason', label: 'Motivo da baixa', required: false, instructions: 'Motivo da baixa patrimonial. Usado quando o status for Baixado.' },
  { key: 'notes', label: 'Observações', required: false, instructions: 'Detalhes complementares.' },
];

const getImportColumnLabel = (column) => `${column.required ? '* ' : ''}${column.label}`;
const NASH_HEADER_BG = '0B5ED7';
const NASH_HEADER_FG = 'FFFFFF';

const buildAssetExportRow = (asset) => ({
  name: asset.name || '',
  tag: asset.tag || '',
  serialNumber: asset.serialNumber || '',
  brand: asset.brand || '',
  status: statusLabels[asset.status] || asset.status || '',
  category: asset.category?.name || '',
  location: asset.location?.name || '',
  acquisitionDate: formatFlexibleDateDisplay(asset.acquisitionDate, asset.acquisitionDatePrecision),
  acquisitionCost: Number(asset.acquisitionCost || 0),
  depreciationRate: Number(asset.depreciationRate || 0),
  usefulLifeMonths: Number(asset.usefulLifeMonths || 0) || '',
  warrantyEnd: asset.warrantyEnd ? toInputDate(asset.warrantyEnd) : '',
  custodian: asset.custodian || '',
  supplier: asset.supplier || '',
  entryOrigin: asset.entryOrigin || '',
  invoiceNumber: asset.invoiceNumber || '',
  disposalDate: asset.disposalDate ? toInputDate(asset.disposalDate) : '',
  disposalReason: asset.disposalReason || '',
  notes: asset.notes || '',
});

const applyNashHeaderStyle = (worksheet, headers) => {
  headers.forEach((_, index) => {
    const cellAddress = XLSXUtils.encode_cell({ c: index, r: 0 });
    if (!worksheet[cellAddress]) return;
    worksheet[cellAddress].s = {
      fill: { fgColor: { rgb: NASH_HEADER_BG } },
      font: { bold: true, color: { rgb: NASH_HEADER_FG } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  });
};

const MAINTENANCE_TYPE_OPTIONS = [
  { value: 'PREVENTIVE', label: 'Preventiva' },
  { value: 'CORRECTIVE', label: 'Corretiva' },
  { value: 'PREDICTIVE', label: 'Preditiva' },
  { value: 'CALIBRATION', label: 'Calibração' },
];

const MAINTENANCE_STATUS_OPTIONS = [
  { value: 'SCHEDULED', label: 'Agendada' },
  { value: 'IN_PROGRESS', label: 'Em execução' },
  { value: 'COMPLETED', label: 'Concluída' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

const MAINTENANCE_PERIODICITY_UNIT_OPTIONS = [
  { value: 'DAYS', label: 'Dias' },
  { value: 'WEEKS', label: 'Semanas' },
  { value: 'MONTHS', label: 'Meses' },
  { value: 'YEARS', label: 'Anos' },
];

const maintenanceTypeLabels = MAINTENANCE_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const maintenanceStatusLabels = MAINTENANCE_STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const emptyAssetForm = {
  id: null,
  name: '',
  serialNumber: '',
  brand: '',
  tag: '',
  status: 'OPERATING',
  categoryId: '',
  locationId: '',
  acquisitionDate: '',
  acquisitionCost: '',
  depreciationRate: '15',
  usefulLifeMonths: '',
  warrantyEnd: '',
  custodian: '',
  responsibleUserIds: [],
  responsibleSectorIds: [],
  supplier: '',
  entryOrigin: '',
  invoiceNumber: '',
  disposalDate: '',
  disposalReason: '',
  notes: '',
  attachments: [],
};

const emptyMaintenanceForm = {
  id: null,
  assetId: null,
  type: 'PREVENTIVE',
  status: 'SCHEDULED',
  recurring: false,
  periodicityValue: '',
  periodicityUnit: 'MONTHS',
  scheduledAt: '',
  performedAt: '',
  responsible: '',
  cost: '',
  notes: '',
};

const emptyCategoryForm = {
  id: null,
  name: '',
  description: '',
  color: '#0ea5e9',
};

const emptyLocationForm = {
  id: null,
  name: '',
  description: '',
  responsible: '',
  contact: '',
};

const resolveErrorMessage = (error) => {
  if (!error) return 'Ocorreu um erro inesperado.';
  if (error.response?.data?.error) return error.response.data.error;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message) return error.message;
  return 'Não foi possível concluir a operação.';
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
};

const parseFlexibleDateInput = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const yearMatch = raw.match(/^(\d{4})$/);
  if (yearMatch) {
    return { year: Number(yearMatch[1]), month: 1, day: 1, precision: 'year' };
  }

  const monthYearMatch = raw.match(/^(?:(\d{1,2})[/-](\d{4})|(\d{4})-(\d{1,2}))$/);
  if (monthYearMatch) {
    const month = Number(monthYearMatch[1] || monthYearMatch[4]);
    const year = Number(monthYearMatch[2] || monthYearMatch[3]);
    if (month >= 1 && month <= 12) {
      return { year, month, day: 1, precision: 'month' };
    }
  }

  const dateMatch = raw.match(/^(?:(\d{4})-(\d{1,2})-(\d{1,2})|(\d{1,2})[/-](\d{1,2})[/-](\d{4}))$/);
  if (dateMatch) {
    const year = Number(dateMatch[1] || dateMatch[6]);
    const month = Number(dateMatch[2] || dateMatch[5]);
    const day = Number(dateMatch[3] || dateMatch[4]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day
    ) {
      return { year, month, day, precision: 'day' };
    }
  }

  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      precision: 'day',
    };
  }

  return null;
};

const flexibleDateToISOString = (value) => {
  const parsed = parseFlexibleDateInput(value);
  if (!parsed) return null;
  return new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day)).toISOString();
};

const getFlexibleDatePrecision = (value) => parseFlexibleDateInput(value)?.precision || null;

const formatFlexibleDateDisplay = (value, precision = 'day') => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  if (precision === 'year') return year;
  if (precision === 'month') return `${month}/${year}`;
  return `${day}/${month}/${year}`;
};

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const toInputDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const [day, time] = date.toISOString().split('T');
  return `${day}T${time.slice(0, 5)}`;
};

const differenceInMonths = (start, end) => {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return 0;
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return 0;
  const startYear = start.getFullYear();
  const startMonth = start.getMonth();
  const endYear = end.getFullYear();
  const endMonth = end.getMonth();
  let months = (endYear - startYear) * 12 + (endMonth - startMonth);
  if (end.getDate() < start.getDate()) {
    months -= 1;
  }
  return Math.max(0, months);
};

const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const diff = target.getTime() - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const formatPercentage = (value) => {
  if (!Number.isFinite(Number(value))) return '0%';
  return `${Math.round(Number(value))}%`;
};

const resolveTextValue = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => resolveTextValue(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const preferredKeys = ['name', 'label', 'title', 'description', 'value'];
    for (const key of preferredKeys) {
      const resolved = resolveTextValue(value[key]);
      if (resolved) return resolved;
    }
  }
  return '';
};

const formatFileSize = (size) => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let current = bytes / 1024;
  let unitIndex = 0;
  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024;
    unitIndex += 1;
  }
  return `${current.toFixed(current >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeHeader = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const parseImportNumber = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes(',') && raw.includes('.')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(',', '.');
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const loadImageAsDataURL = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Erro ao carregar a imagem.'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Erro ao carregar imagem do relatório:', error);
    return null;
  }
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Não foi possível processar o anexo.'));
    reader.readAsDataURL(file);
  });

const dataUrlToBlob = (dataUrl) => {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
  const separatorIndex = dataUrl.indexOf(',');
  if (separatorIndex < 0) return null;
  const metadata = dataUrl.slice(5, separatorIndex);
  const content = dataUrl.slice(separatorIndex + 1);
  const isBase64 = metadata.includes(';base64');
  const mimeType = metadata.split(';')[0] || 'application/octet-stream';

  try {
    if (isBase64) {
      const binary = window.atob(content);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }
      return new Blob([bytes], { type: mimeType });
    }
    return new Blob([decodeURIComponent(content)], { type: mimeType });
  } catch (error) {
    console.error('Erro ao converter anexo em blob:', error);
    return null;
  }
};

const createChartImage = async (config, dimensions = {}) => {
  if (typeof window === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const width = Number(dimensions.width) > 0 ? Number(dimensions.width) : 1000;
  const height = Number(dimensions.height) > 0 ? Number(dimensions.height) : 560;
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const providedOptions = config.options || {};
  const providedPlugins = providedOptions.plugins || {};

  const legendLabels = {
    font: {
      family: 'Helvetica',
      size: 14,
      ...(providedPlugins.legend?.labels?.font || {}),
    },
    color: '#0f172a',
    ...(providedPlugins.legend?.labels || {}),
  };

  const legendConfig = {
    ...providedPlugins.legend,
    labels: legendLabels,
  };

  const titleConfig = {
    color: '#0f172a',
    font: {
      family: 'Helvetica',
      size: 18,
      ...(providedPlugins.title?.font || {}),
    },
    padding: {
      top: 12,
      bottom: 12,
      ...(providedPlugins.title?.padding || {}),
    },
    ...providedPlugins.title,
  };

  if (typeof titleConfig.display === 'undefined') {
    titleConfig.display = Boolean(titleConfig.text);
  }

  const chart = new Chart(context, {
    ...config,
    options: {
      responsive: false,
      animation: false,
      ...providedOptions,
      plugins: {
        ...providedPlugins,
        legend: legendConfig,
        title: titleConfig,
      },
    },
  });

  await new Promise((resolve) => setTimeout(resolve, 150));
  const image = chart.toBase64Image();
  chart.destroy();
  return { image, width, height };
};

const computeAssetMetrics = (asset) => {
  const acquisitionCost = Number(asset.acquisitionCost || 0);
  const depreciationRate = Number(asset.depreciationRate || 0);
  const acquisitionDate = asset.acquisitionDate ? new Date(asset.acquisitionDate) : null;
  const now = new Date();
  const monthsInUse = acquisitionDate ? differenceInMonths(acquisitionDate, now) : 0;
  const monthlyRate = (acquisitionCost * depreciationRate) / (12 * 100);
  const accumulatedDepreciation = Math.min(
    acquisitionCost,
    Math.max(0, monthsInUse) * Math.max(0, monthlyRate),
  );
  const currentValue = Math.max(0, acquisitionCost - accumulatedDepreciation);
  const depreciationPercentage = acquisitionCost
    ? Math.min(100, (accumulatedDepreciation / acquisitionCost) * 100)
    : 0;

  const maintenance = Array.isArray(asset.maintenance) ? asset.maintenance : [];
  const openMaintenance = maintenance.filter((entry) => entry.status !== 'COMPLETED');
  const upcomingMaintenance = openMaintenance
    .filter((entry) => entry.scheduledAt)
    .slice()
    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))[0] || null;

  return {
    acquisitionCost,
    monthsInUse,
    accumulatedDepreciation,
    currentValue,
    depreciationPercentage,
    openMaintenanceCount: openMaintenance.length,
    upcomingMaintenance,
  };
};

const statusBadgeStyles = {
  OPERATING: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  MAINTENANCE: 'bg-amber-100 text-amber-800 border-amber-200',
  STOCK: 'bg-slate-100 text-slate-700 border-slate-200',
  DISPOSED: 'bg-rose-100 text-rose-700 border-rose-200',
};

const statusLabels = {
  OPERATING: 'Em operação',
  MAINTENANCE: 'Em manutenção',
  STOCK: 'Em estoque',
  DISPOSED: 'Baixado',
};

const chartPalette = [
  '#2563eb',
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#f43f5e',
  '#8b5cf6',
  '#14b8a6',
  '#f59e0b',
  '#475569',
];


const buildPieGradient = (values, total) => {
  if (!total) return '#e2e8f0';
  let cursor = 0;
  const segments = values.map((value, index) => {
    const start = cursor;
    const end = cursor + (Number(value || 0) / total) * 100;
    cursor = end;
    const color = chartPalette[index % chartPalette.length];
    return `${color} ${start}% ${end}%`;
  });
  return `conic-gradient(${segments.join(', ')})`;
};

const transparentizeHex = (hex, alpha = 0.14) => {
  const cleanHex = hex.replace('#', '');
  const bigint = parseInt(cleanHex, 16);
  const red = (bigint >> 16) & 255;
  const green = (bigint >> 8) & 255;
  const blue = bigint & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const PDF_ASSET_COLUMNS = [
  { key: 'name', label: 'Ativo', weight: 18, getValue: (asset) => resolveTextValue(asset.name) || resolveTextValue(asset.tag) || 'Ativo sem nome' },
  { key: 'brand', label: 'Marca', weight: 10, getValue: (asset) => asset.brand || '—' },
  { key: 'tag', label: 'Etiqueta', weight: 10, getValue: (asset) => asset.tag || '—' },
  { key: 'serialNumber', label: 'Nº série', weight: 12, getValue: (asset) => asset.serialNumber || '—' },
  { key: 'category', label: 'Categoria', weight: 14, getValue: (asset) => resolveTextValue(asset.category) || '—' },
  { key: 'location', label: 'Local', weight: 13, getValue: (asset) => resolveTextValue(asset.location) || '—' },
  { key: 'status', label: 'Status', weight: 10, getValue: (asset) => (asset.status ? statusLabels[asset.status] || asset.status : '—') },
  { key: 'acquisitionDate', label: 'Aquisição', weight: 11, getValue: (asset) => formatFlexibleDateDisplay(asset.acquisitionDate, asset.acquisitionDatePrecision) || '—' },
  { key: 'acquisitionCost', label: 'Valor', weight: 12, getValue: (asset) => formatCurrency(asset.acquisitionCost) },
  { key: 'supplier', label: 'Fornecedor', weight: 13, getValue: (asset) => asset.supplier || '—' },
  { key: 'entryOrigin', label: 'Origem', weight: 11, getValue: (asset) => asset.entryOrigin || '—' },
  { key: 'invoiceNumber', label: 'N° NF', weight: 10, getValue: (asset) => asset.invoiceNumber || '—' },
  { key: 'disposalDate', label: 'Baixa', weight: 11, getValue: (asset) => formatDate(asset.disposalDate) },
  { key: 'disposalReason', label: 'Motivo baixa', weight: 16, getValue: (asset) => asset.disposalReason || '—' },
  { key: 'custodian', label: 'Responsável', weight: 13, getValue: (asset) => asset.custodian || '—' },
  { key: 'notes', label: 'Observações', weight: 18, getValue: (asset) => asset.notes || '—' },
];

const DEFAULT_PDF_ASSET_FIELDS = ['name', 'category', 'location', 'status', 'entryOrigin'];

const TAB_ITEMS = [
  { id: 'overview', label: 'Visão geral', icon: FaChartLine },
  { id: 'ai', label: 'Insights IA', icon: FaRobot },
  { id: 'assets', label: 'Ativos', icon: FaClipboardList },
  { id: 'import', label: 'Importação', icon: FaFileImport },
  { id: 'maintenance', label: 'Manutenções', icon: FaTools },
  { id: 'directories', label: 'Cadastros', icon: FaCogs },
];

const AssetLoadingBar = () => (
  <div className="rounded-2xl border border-sky-100 bg-sky-50/80 p-4 shadow-sm" role="status" aria-live="polite">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-semibold text-sky-900">Carregando patrimônios</p>
        <p className="text-xs text-sky-700">
          Aguarde enquanto todos os ativos, categorias e locais são carregados.
        </p>
      </div>
      <span className="text-xs font-medium text-sky-700">Sincronizando dados...</span>
    </div>
    <div className="mt-3 h-2 overflow-hidden rounded-full bg-sky-100">
      <div className="asset-loading-bar h-full rounded-full bg-gradient-to-r from-sky-500 via-blue-600 to-sky-400" />
    </div>
  </div>
);

export default function AssetManagement() {
  const { user } = useAuth();
  const canManage = hasPermission(user, ASSET_MANAGE_PERMISSION);
  const canView = canManage || hasPermission(user, ASSET_VIEW_PERMISSION);

  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetPage, setAssetPage] = useState(1);
  const [assetPageSize, setAssetPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importingAssets, setImportingAssets] = useState(false);

  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    categoryId: 'all',
    locationId: 'all',
    entryOrigin: '',
    supplier: '',
    acquisitionDate: '',
    disposalDate: '',
    disposalReason: '',
    notes: '',
  });

  const [activeTab, setActiveTab] = useState('overview');

  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetForm, setAssetForm] = useState(emptyAssetForm);
  const [savingAsset, setSavingAsset] = useState(false);
  const [calendarRecipients, setCalendarRecipients] = useState({ users: [], sectors: [] });
  const [reminderDefaults, setReminderDefaults] = useState({ userIds: [], sectorIds: [] });
  const [savingReminderDefaults, setSavingReminderDefaults] = useState(false);

  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState(emptyMaintenanceForm);
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [savingCategory, setSavingCategory] = useState(false);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationForm, setLocationForm] = useState(emptyLocationForm);
  const [savingLocation, setSavingLocation] = useState(false);
  const [pdfOrientationModalOpen, setPdfOrientationModalOpen] = useState(false);
  const [selectedPdfAssetFields, setSelectedPdfAssetFields] = useState(DEFAULT_PDF_ASSET_FIELDS);

  const showFeedback = useCallback((message, type = 'success') => {
    setFeedback(message);
    setFeedbackType(type);
    if (message) {
      setTimeout(() => setFeedback(''), 5000);
    }
  }, []);

  const handleError = useCallback((message) => {
    setError(message);
    if (message) {
      setTimeout(() => setError(''), 7000);
    }
  }, []);

  const requireAssetManagementPermission = useCallback(() => {
    if (canManage) return true;
    handleError('Você não tem permissão para gerenciar patrimônio.');
    return false;
  }, [canManage, handleError]);

  const loadAssets = useCallback(async () => {
    try {
      const response = await AssetService.listAssets();
      const data = Array.isArray(response.data) ? response.data : [];
      setAssets(data);
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setAssets([]);
    }
  }, [handleError]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await AssetService.listCategories();
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setCategories([]);
    }
  }, [handleError]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await AssetService.listLocations();
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setLocations([]);
    }
  }, [handleError]);

  const loadAll = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const [snapshotResult, recipientsResult] = await Promise.allSettled([
        AssetService.getSnapshot(),
        api.get('/calendar/recipients'),
      ]);

      if (snapshotResult.status === 'fulfilled') {
        const snapshot = snapshotResult.value?.data || {};
        setAssets(Array.isArray(snapshot.assets) ? snapshot.assets : []);
        setCategories(Array.isArray(snapshot.categories) ? snapshot.categories : []);
        setLocations(Array.isArray(snapshot.locations) ? snapshot.locations : []);
        setReminderDefaults({
          userIds: Array.isArray(snapshot.reminderDefaults?.userIds)
            ? snapshot.reminderDefaults.userIds.map(String)
            : [],
          sectorIds: Array.isArray(snapshot.reminderDefaults?.sectorIds)
            ? snapshot.reminderDefaults.sectorIds.map(String)
            : [],
        });
      } else {
        handleError(resolveErrorMessage(snapshotResult.reason));
        setAssets([]);
        setCategories([]);
        setLocations([]);
        setReminderDefaults({ userIds: [], sectorIds: [] });
      }

      if (recipientsResult.status === 'fulfilled') {
        setCalendarRecipients({
          users: Array.isArray(recipientsResult.value?.data?.users)
            ? recipientsResult.value.data.users
            : [],
          sectors: Array.isArray(recipientsResult.value?.data?.sectors)
            ? recipientsResult.value.data.sectors
            : [],
        });
      } else {
        setCalendarRecipients({ users: [], sectors: [] });
      }
    } finally {
      setLoading(false);
    }
  }, [canView, handleError]);

  useEffect(() => {
    if (!canView) return;
    loadAll();
  }, [canView, loadAll]);

  const assetsWithMetrics = useMemo(
    () => assets.map((asset) => ({ ...asset, ...computeAssetMetrics(asset) })),
    [assets],
  );

  const filteredAssets = useMemo(() => {
    const term = normalizeText(filters.search);
    const entryOriginTerm = normalizeText(filters.entryOrigin);
    const supplierTerm = normalizeText(filters.supplier);
    const notesTerm = normalizeText(filters.notes);
    const disposalReasonTerm = normalizeText(filters.disposalReason);
    const acquisitionFilter = parseFlexibleDateInput(filters.acquisitionDate);

    return assetsWithMetrics.filter((asset) => {
      const matchesTerm = term
        ? [
            asset.name,
            asset.serialNumber,
            asset.brand,
            asset.invoiceNumber,
            asset.disposalReason,
            asset.tag,
            asset.custodian,
            asset.category?.name,
            asset.location?.name,
          ]
            .filter(Boolean)
            .some((candidate) => normalizeText(candidate).includes(term))
        : true;

      const matchesStatus =
        filters.status === 'all' || asset.status === filters.status;

      const matchesCategory =
        filters.categoryId === 'all' || asset.category?.id === filters.categoryId;

      const matchesLocation =
        filters.locationId === 'all' || asset.location?.id === filters.locationId;

      const matchesEntryOrigin = entryOriginTerm
        ? normalizeText(asset.entryOrigin).includes(entryOriginTerm)
        : true;

      const matchesSupplier = supplierTerm
        ? normalizeText(asset.supplier).includes(supplierTerm)
        : true;

      const matchesNotes = notesTerm
        ? normalizeText(asset.notes).includes(notesTerm)
        : true;

      const assetAcquisitionDate = asset.acquisitionDate ? new Date(asset.acquisitionDate) : null;
      const matchesAcquisitionDate = acquisitionFilter
        ? assetAcquisitionDate
          && !Number.isNaN(assetAcquisitionDate.getTime())
          && assetAcquisitionDate.getUTCFullYear() === acquisitionFilter.year
          && (acquisitionFilter.precision === 'year'
            || assetAcquisitionDate.getUTCMonth() + 1 === acquisitionFilter.month)
          && (acquisitionFilter.precision !== 'day'
            || assetAcquisitionDate.getUTCDate() === acquisitionFilter.day)
        : true;

      const shouldApplyDisposalFilters = filters.status === 'DISPOSED';
      const matchesDisposalDate = shouldApplyDisposalFilters && filters.disposalDate
        ? toInputDate(asset.disposalDate) === filters.disposalDate
        : true;
      const matchesDisposalReason = shouldApplyDisposalFilters && disposalReasonTerm
        ? normalizeText(asset.disposalReason).includes(disposalReasonTerm)
        : true;

      return matchesTerm
        && matchesStatus
        && matchesCategory
        && matchesLocation
        && matchesEntryOrigin
        && matchesSupplier
        && matchesAcquisitionDate
        && matchesDisposalDate
        && matchesDisposalReason
        && matchesNotes;
    });
  }, [assetsWithMetrics, filters]);

  const sortedFilteredAssets = useMemo(
    () =>
      filteredAssets
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' })),
    [filteredAssets],
  );

  useEffect(() => {
    setAssetPage(1);
  }, [filters, assetPageSize]);

  const assetPagination = paginateTableRows(
    sortedFilteredAssets,
    assetPage,
    assetPageSize,
  );
  const paginatedAssets = assetPagination.rows;

  useEffect(() => {
    if (assetPage !== assetPagination.page) {
      setAssetPage(assetPagination.page);
    }
  }, [assetPage, assetPagination.page]);

  const filteredTotals = useMemo(() => {
    const totalAcquisition = filteredAssets.reduce(
      (total, asset) => total + Number(asset.acquisitionCost || 0),
      0,
    );
    const totalCurrent = filteredAssets.reduce(
      (total, asset) => total + Number(asset.currentValue || 0),
      0,
    );
    const averageDepreciation = filteredAssets.length
      ? filteredAssets.reduce(
          (total, asset) => total + Number(asset.depreciationPercentage || 0),
          0,
        ) / filteredAssets.length
      : 0;

    return {
      totalAcquisition,
      totalCurrent,
      averageDepreciation,
      assetCount: filteredAssets.length,
    };
  }, [filteredAssets]);

  const statusDistributionSummary = useMemo(() => {
    if (!filteredAssets.length) {
      return { labels: [], values: [] };
    }
    const counts = filteredAssets.reduce((acc, asset) => {
      const key = asset.status || 'UNKNOWN';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(counts).map((status) => statusLabels[status] || status);
    const values = Object.values(counts);
    return { labels, values };
  }, [filteredAssets]);

  const categoryDistributionSummary = useMemo(() => {
    if (!filteredAssets.length) {
      return { labels: [], values: [] };
    }
    const counts = filteredAssets.reduce((acc, asset) => {
      const key = asset.category?.name || 'Sem categoria';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(counts);
    const values = Object.values(counts);
    return { labels, values };
  }, [filteredAssets]);

  const warrantySummary = useMemo(() => {
    const expiring = [];
    const expired = [];
    assetsWithMetrics.forEach((asset) => {
      const days = daysUntil(asset.warrantyEnd);
      if (days === null) return;
      if (days < 0) {
        expired.push(asset);
      } else if (days <= 45) {
        expiring.push({ ...asset, days });
      }
    });
    expiring.sort((a, b) => a.days - b.days);
    return { expiring, expired };
  }, [assetsWithMetrics]);

  const upcomingMaintenance = useMemo(() => {
    const entries = [];
    assetsWithMetrics.forEach((asset) => {
      (asset.maintenance || []).forEach((entry) => {
        entries.push({ asset, entry });
      });
    });
    return entries
      .filter(({ entry }) => entry.status !== 'COMPLETED')
      .sort((a, b) => {
        const dateA = new Date(a.entry.scheduledAt || a.entry.performedAt || 0).getTime();
        const dateB = new Date(b.entry.scheduledAt || b.entry.performedAt || 0).getTime();
        return dateA - dateB;
      });
  }, [assetsWithMetrics]);

  const maintenanceHistory = useMemo(() => {
    const entries = [];
    assetsWithMetrics.forEach((asset) => {
      (asset.maintenance || []).forEach((entry) => {
        entries.push({ asset, entry });
      });
    });
    return entries
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a.entry.performedAt || a.entry.scheduledAt || 0).getTime();
        const dateB = new Date(b.entry.performedAt || b.entry.scheduledAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 12);
  }, [assetsWithMetrics]);

  const totalAcquisitionValue = useMemo(
    () =>
      assetsWithMetrics.reduce(
        (total, asset) => total + Number(asset.acquisitionCost || 0),
        0,
      ),
    [assetsWithMetrics],
  );

  const totalCurrentValue = useMemo(
    () => assetsWithMetrics.reduce((total, asset) => total + asset.currentValue, 0),
    [assetsWithMetrics],
  );

  const pendingMaintenanceCount = useMemo(
    () =>
      assetsWithMetrics.reduce(
        (total, asset) => total + Number(asset.openMaintenanceCount || 0),
        0,
      ),
    [assetsWithMetrics],
  );

  const highlyDepreciatedAssets = useMemo(
    () => assetsWithMetrics.filter((asset) => asset.depreciationPercentage >= 70).length,
    [assetsWithMetrics],
  );

  const topDepreciatedAssets = useMemo(() => {
    const candidates = assetsWithMetrics.filter((asset) =>
      Number.isFinite(Number(asset.depreciationPercentage)),
    );
    return candidates
      .slice()
      .sort(
        (a, b) =>
          Number(b.depreciationPercentage || 0) - Number(a.depreciationPercentage || 0),
      )
      .slice(0, 5);
  }, [assetsWithMetrics]);

  const openAssetModal = useCallback(
    async (asset = null) => {
      if (!requireAssetManagementPermission()) return;
      if (asset) {
        let assetDetails = asset;
        try {
          const response = await AssetService.getAsset(asset.id);
          assetDetails = response.data || asset;
        } catch (err) {
          handleError(resolveErrorMessage(err));
          return;
        }
        setAssetForm({
          id: assetDetails.id,
          name: assetDetails.name || '',
          serialNumber: assetDetails.serialNumber || '',
          brand: assetDetails.brand || '',
          tag: assetDetails.tag || '',
          status: assetDetails.status || 'OPERATING',
          categoryId: assetDetails.category?.id || '',
          locationId: assetDetails.location?.id || '',
          acquisitionDate: formatFlexibleDateDisplay(assetDetails.acquisitionDate, assetDetails.acquisitionDatePrecision),
          acquisitionCost: assetDetails.acquisitionCost ? String(assetDetails.acquisitionCost) : '',
          depreciationRate: assetDetails.depreciationRate ? String(assetDetails.depreciationRate) : '',
          usefulLifeMonths: assetDetails.usefulLifeMonths ? String(assetDetails.usefulLifeMonths) : '',
          warrantyEnd: toInputDate(assetDetails.warrantyEnd),
          custodian: assetDetails.custodian || '',
          responsibleUserIds: Array.isArray(assetDetails.responsibleUserIds)
            ? assetDetails.responsibleUserIds.map(String)
            : assetDetails.responsibleUserId
              ? [String(assetDetails.responsibleUserId)]
              : [],
          responsibleSectorIds: Array.isArray(assetDetails.responsibleSectorIds)
            ? assetDetails.responsibleSectorIds.map(String)
            : assetDetails.responsibleSectorId
              ? [String(assetDetails.responsibleSectorId)]
              : [],
          supplier: assetDetails.supplier || '',
          entryOrigin: assetDetails.entryOrigin || '',
          invoiceNumber: assetDetails.invoiceNumber || '',
          disposalDate: toInputDate(assetDetails.disposalDate),
          disposalReason: assetDetails.disposalReason || '',
          notes: assetDetails.notes || '',
          attachments: Array.isArray(assetDetails.attachments) ? assetDetails.attachments : [],
        });
      } else {
        setAssetForm({
          ...emptyAssetForm,
          responsibleUserIds: reminderDefaults.userIds || [],
          responsibleSectorIds: reminderDefaults.sectorIds || [],
        });
      }
      setError('');
      setFeedback('');
      setAssetModalOpen(true);
    },
    [requireAssetManagementPermission, reminderDefaults, handleError],
  );

  const openMaintenanceModal = useCallback((asset = null) => {
    if (!requireAssetManagementPermission()) return;
    setMaintenanceForm({
      ...emptyMaintenanceForm,
      assetId: asset?.id || '',
      responsible: asset?.custodian || '',
    });
    setError('');
    setMaintenanceModalOpen(true);
  }, [requireAssetManagementPermission]);

  const openCategoryModal = useCallback((category = null) => {
    if (!requireAssetManagementPermission()) return;
    if (category) {
      setCategoryForm({
        id: category.id,
        name: category.name || '',
        description: category.description || '',
        color: category.color || '#0ea5e9',
      });
    } else {
      setCategoryForm(emptyCategoryForm);
    }
    setCategoryModalOpen(true);
  }, [requireAssetManagementPermission]);

  const openLocationModal = useCallback((location = null) => {
    if (!requireAssetManagementPermission()) return;
    if (location) {
      setLocationForm({
        id: location.id,
        name: location.name || '',
        description: location.description || '',
        responsible: location.responsible || '',
        contact: location.contact || '',
      });
    } else {
      setLocationForm(emptyLocationForm);
    }
    setLocationModalOpen(true);
  }, [requireAssetManagementPermission]);

  const handleReminderSelectionChange = useCallback((field, selectedOptions) => {
    const values = Array.from(selectedOptions).map((option) => option.value);
    setAssetForm((prev) => ({ ...prev, [field]: values }));
  }, []);

  const handleDefaultReminderSelectionChange = useCallback((field, selectedOptions) => {
    const values = Array.from(selectedOptions).map((option) => option.value);
    setReminderDefaults((prev) => ({ ...prev, [field]: values }));
  }, []);

  const handleSaveReminderDefaults = useCallback(async () => {
    if (!requireAssetManagementPermission()) return;
    setSavingReminderDefaults(true);
    try {
      const response = await AssetService.updateReminderDefaults(reminderDefaults);
      setReminderDefaults({
        userIds: Array.isArray(response.data?.userIds) ? response.data.userIds.map(String) : [],
        sectorIds: Array.isArray(response.data?.sectorIds) ? response.data.sectorIds.map(String) : [],
      });
      showFeedback('Configuração padrão de lembretes salva com sucesso.');
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingReminderDefaults(false);
    }
  }, [reminderDefaults, showFeedback, handleError, requireAssetManagementPermission]);

  const handleSaveAsset = useCallback(async () => {
    if (!requireAssetManagementPermission()) return;
    setSavingAsset(true);
    try {
      const payload = {
        ...assetForm,
        acquisitionDate: flexibleDateToISOString(assetForm.acquisitionDate),
        acquisitionDatePrecision: getFlexibleDatePrecision(assetForm.acquisitionDate),
        acquisitionCost: assetForm.acquisitionCost
          ? Number(assetForm.acquisitionCost)
          : 0,
        depreciationRate: assetForm.depreciationRate
          ? Number(assetForm.depreciationRate)
          : 0,
        usefulLifeMonths: assetForm.usefulLifeMonths
          ? Number(assetForm.usefulLifeMonths)
          : null,
        entryOrigin: assetForm.entryOrigin,
        invoiceNumber: assetForm.invoiceNumber,
        disposalDate: assetForm.status === 'DISPOSED' ? assetForm.disposalDate : '',
        disposalReason: assetForm.status === 'DISPOSED' ? assetForm.disposalReason : '',
        attachments: Array.isArray(assetForm.attachments) ? assetForm.attachments : [],
        responsibleUserIds: Array.isArray(assetForm.responsibleUserIds) ? assetForm.responsibleUserIds : [],
        responsibleSectorIds: Array.isArray(assetForm.responsibleSectorIds) ? assetForm.responsibleSectorIds : [],
      };
      const response = assetForm.id
        ? await AssetService.updateAsset(assetForm.id, payload)
        : await AssetService.createAsset(payload);
      const savedAsset = response?.data;
      if (savedAsset?.id) {
        setAssets((current) => {
          const existingIndex = current.findIndex((asset) => asset.id === savedAsset.id);
          if (existingIndex < 0) return [...current, savedAsset];
          return current.map((asset, index) => (index === existingIndex ? savedAsset : asset));
        });
      }
      showFeedback(assetForm.id ? 'Ativo atualizado com sucesso.' : 'Ativo cadastrado com sucesso.');
      setAssetModalOpen(false);
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingAsset(false);
    }
  }, [assetForm, showFeedback, handleError, requireAssetManagementPermission]);

  const handleAddAssetAttachments = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      try {
        const nextAttachments = await Promise.all(
          Array.from(files).map(async (file) => ({
            id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
            name: file.name || 'anexo',
            url: await readFileAsDataUrl(file),
            type: file.type || 'application/octet-stream',
            size: Number(file.size) || 0,
            uploadedAt: new Date().toISOString(),
          })),
        );
        setAssetForm((prev) => ({
          ...prev,
          attachments: [...(Array.isArray(prev.attachments) ? prev.attachments : []), ...nextAttachments],
        }));
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [handleError],
  );

  const handleRemoveAssetAttachment = useCallback((attachmentId) => {
    setAssetForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((attachment) => attachment.id !== attachmentId),
    }));
  }, []);

  const handleOpenAssetAttachment = useCallback(
    (attachment) => {
      if (!attachment?.url) {
        handleError('Abra o cadastro do ativo para carregar o conteúdo do anexo.');
        return;
      }

      if (!attachment.url.startsWith('data:')) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
        return;
      }

      const blob = dataUrlToBlob(attachment.url);
      if (!blob) {
        handleError('Não foi possível abrir o anexo selecionado.');
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    },
    [handleError],
  );

  const handleDeleteAsset = useCallback(
    async (asset) => {
      if (!requireAssetManagementPermission()) return;
      if (!asset || !window.confirm(`Excluir o ativo "${asset.name}"?`)) return;
      try {
        await AssetService.deleteAsset(asset.id);
        showFeedback('Ativo excluído com sucesso.');
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadAssets, showFeedback, handleError, requireAssetManagementPermission],
  );

  const handleSaveMaintenance = useCallback(async () => {
    if (!requireAssetManagementPermission()) return;
    if (!maintenanceForm.assetId) return;
    setSavingMaintenance(true);
    try {
      const payload = {
        type: maintenanceForm.type,
        status: maintenanceForm.status,
        recurring: Boolean(maintenanceForm.recurring),
        periodicityValue: maintenanceForm.recurring && maintenanceForm.periodicityValue
          ? Number(maintenanceForm.periodicityValue)
          : null,
        periodicityUnit: maintenanceForm.recurring
          ? maintenanceForm.periodicityUnit
          : 'MONTHS',
        scheduledAt: maintenanceForm.scheduledAt,
        performedAt: maintenanceForm.performedAt,
        responsible: maintenanceForm.responsible,
        cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : 0,
        notes: maintenanceForm.notes,
      };
      if (maintenanceForm.id) {
        await AssetService.updateMaintenance(
          maintenanceForm.assetId,
          maintenanceForm.id,
          payload,
        );
        showFeedback('Manutenção atualizada com sucesso.');
      } else {
        await AssetService.recordMaintenance(maintenanceForm.assetId, payload);
        showFeedback('Manutenção registrada com sucesso.');
      }
      setMaintenanceModalOpen(false);
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingMaintenance(false);
    }
  }, [maintenanceForm, loadAssets, showFeedback, handleError, requireAssetManagementPermission]);

  const handleCompleteMaintenance = useCallback(
    async (assetId, maintenance) => {
      if (!requireAssetManagementPermission()) return;
      try {
        await AssetService.updateMaintenance(assetId, maintenance.id, {
          status: 'COMPLETED',
          performedAt: new Date().toISOString(),
        });
        showFeedback('Manutenção concluída.');
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadAssets, showFeedback, handleError, requireAssetManagementPermission],
  );

  const handleDeleteMaintenance = useCallback(
    async (assetId, maintenance) => {
      if (!requireAssetManagementPermission()) return;
      if (!window.confirm('Remover o registro de manutenção selecionado?')) return;
      try {
        await AssetService.deleteMaintenance(assetId, maintenance.id);
        showFeedback('Registro de manutenção excluído.');
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadAssets, showFeedback, handleError, requireAssetManagementPermission],
  );

  const handleSaveCategory = useCallback(async () => {
    if (!requireAssetManagementPermission()) return;
    setSavingCategory(true);
    try {
      if (categoryForm.id) {
        await AssetService.updateCategory(categoryForm.id, categoryForm);
        showFeedback('Categoria atualizada com sucesso.');
      } else {
        await AssetService.createCategory(categoryForm);
        showFeedback('Categoria criada com sucesso.');
      }
      setCategoryModalOpen(false);
      await loadCategories();
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingCategory(false);
    }
  }, [categoryForm, loadCategories, loadAssets, showFeedback, handleError, requireAssetManagementPermission]);

  const handleDeleteCategory = useCallback(
    async (category) => {
      if (!requireAssetManagementPermission()) return;
      if (!category) return;
      if (!window.confirm(`Excluir a categoria "${category.name}"?`)) return;
      try {
        await AssetService.deleteCategory(category.id);
        showFeedback('Categoria excluída com sucesso.');
        await loadCategories();
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadCategories, loadAssets, showFeedback, handleError, requireAssetManagementPermission],
  );

  const handleSaveLocation = useCallback(async () => {
    if (!requireAssetManagementPermission()) return;
    setSavingLocation(true);
    try {
      if (locationForm.id) {
        await AssetService.updateLocation(locationForm.id, locationForm);
        showFeedback('Localização atualizada com sucesso.');
      } else {
        await AssetService.createLocation(locationForm);
        showFeedback('Localização criada com sucesso.');
      }
      setLocationModalOpen(false);
      await loadLocations();
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingLocation(false);
    }
  }, [locationForm, loadLocations, loadAssets, showFeedback, handleError, requireAssetManagementPermission]);

  const handleDeleteLocation = useCallback(
    async (location) => {
      if (!requireAssetManagementPermission()) return;
      if (!location) return;
      if (!window.confirm(`Excluir a localização "${location.name}"?`)) return;
      try {
        await AssetService.deleteLocation(location.id);
        showFeedback('Localização excluída com sucesso.');
        await loadLocations();
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadLocations, loadAssets, showFeedback, handleError, requireAssetManagementPermission],
  );

  const handleExportCsv = useCallback(() => {
    if (sortedFilteredAssets.length === 0) {
      handleError('Não há dados para exportar.');
      return;
    }
    try {
      const header = IMPORT_COLUMNS.map((column) => column.label);
      const rows = sortedFilteredAssets.map((asset) => {
        const row = buildAssetExportRow(asset);
        return IMPORT_COLUMNS.map((column) => row[column.key] ?? '');
      });

      const csv = [header, ...rows]
        .map((row) =>
          row
            .map((value) => {
              const text = String(value ?? '').replace(/"/g, '""');
              return `"${text}"`;
            })
            .join(';'),
        )
        .join('\n');

      if (typeof window !== 'undefined' && window.document) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute(
          'download',
          `patrimonio-${new Date().toISOString().slice(0, 10)}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        showFeedback('Relatório exportado com sucesso.');
      }
    } catch (err) {
      handleError('Não foi possível exportar o relatório.');
    }
  }, [sortedFilteredAssets, showFeedback, handleError]);

  const handleExportExcel = useCallback(
    (format) => {
      if (sortedFilteredAssets.length === 0) {
        handleError('Não há dados para exportar.');
        return;
      }

      try {
        const header = IMPORT_COLUMNS.map((column) => column.label);
        const rows = sortedFilteredAssets.map((asset) => {
          const row = buildAssetExportRow(asset);
          return IMPORT_COLUMNS.map((column) => row[column.key] ?? '');
        });

        const worksheet = XLSXUtils.aoa_to_sheet([header, ...rows]);
        worksheet['!cols'] = IMPORT_COLUMNS.map((column) => ({ wch: Math.max(column.label.length + 3, 20) }));
        applyNashHeaderStyle(worksheet, header);
        const workbook = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(workbook, worksheet, 'Patrimônio');

        const suffix = new Date().toISOString().slice(0, 10);
        const filename = `patrimonio-${suffix}.${format}`;
        const bookType = format === 'xls' ? 'biff8' : 'xlsx';

        writeXLSXFile(workbook, filename, { bookType });
        showFeedback('Relatório exportado com sucesso.');
      } catch (err) {
        console.error('Erro ao exportar planilha de patrimônio:', err);
        handleError('Não foi possível exportar o relatório.');
      }
    },
    [sortedFilteredAssets, showFeedback, handleError],
  );

  const handleExportPdf = useCallback(async (orientation = 'portrait') => {
    if (sortedFilteredAssets.length === 0) {
      handleError('Não há dados para exportar.');
      return;
    }

    const selectedPdfColumns = PDF_ASSET_COLUMNS.filter((column) =>
      selectedPdfAssetFields.includes(column.key),
    );

    if (!selectedPdfColumns.length) {
      handleError('Selecione ao menos um campo para aparecer no PDF.');
      return;
    }

    try {
      const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 40;
      const contentWidth = pageWidth - margin * 2;
      let cursorY = 70;

      const logoPath = `${process.env.PUBLIC_URL || ''}/nashlogoazul.png`;
      const logoDataUrl = await loadImageAsDataURL(logoPath);
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', margin, cursorY - 30, 70, 70);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(15, 23, 42);
      doc.text('Relatório de Patrimônio', margin + 90, cursorY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(
        'Relatório gerado pelo sistema Intelligent BPM Nash Tecnologia. Site: www.nashtec.com.br',
        margin + 90,
        cursorY + 20,
      );

      cursorY += 70;
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, cursorY, pageWidth - margin, cursorY);
      cursorY += 30;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(15, 23, 42);
      doc.text('Visão geral', margin, cursorY);
      cursorY += 22;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(71, 85, 105);
      const summaryLines = [
        `Total de ativos monitorados: ${filteredTotals.assetCount}`,
        `Valor de aquisição acumulado: ${formatCurrency(filteredTotals.totalAcquisition)}`,
        `Valor contábil estimado: ${formatCurrency(filteredTotals.totalCurrent)}`,
        `Depreciação média: ${formatPercentage(filteredTotals.averageDepreciation)}`,
      ];

      summaryLines.forEach((line) => {
        doc.text(line, margin, cursorY);
        cursorY += 18;
      });

      cursorY += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text('Indicadores visuais', margin, cursorY);
      cursorY += 16;

      const chartAreaWidth = contentWidth;
      const isLandscape = orientation === 'landscape';
      const doughnutSize = Math.min(contentWidth * (isLandscape ? 0.5 : 0.72), 380);
      const barChartHeight = isLandscape ? 280 : 240;
      const chartSpacing = 48;
      const chartImages = [];

      if (statusDistributionSummary.labels.length) {
        const statusChart = await createChartImage({
          type: 'doughnut',
          data: {
            labels: statusDistributionSummary.labels,
            datasets: [
              {
                data: statusDistributionSummary.values,
                backgroundColor: chartPalette.slice(0, statusDistributionSummary.labels.length),
                borderWidth: 0,
              },
            ],
          },
          options: {
            cutout: '58%',
            layout: {
              padding: { top: 8, right: 20, bottom: 6, left: 20 },
            },
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 14,
                  boxHeight: 14,
                  padding: 14,
                },
              },
              title: {
                text: 'Distribuição por status',
              },
            },
          },
        }, { width: 1000, height: 1000 });
        if (statusChart) {
          chartImages.push({ ...statusChart, targetWidth: doughnutSize, targetHeight: doughnutSize });
        }
      }

      if (categoryDistributionSummary.labels.length) {
        const categoryChart = await createChartImage({
          type: 'bar',
          data: {
            labels: categoryDistributionSummary.labels,
            datasets: [
              {
                label: 'Ativos',
                data: categoryDistributionSummary.values,
                backgroundColor: chartPalette.slice(0, categoryDistributionSummary.labels.length),
                borderRadius: 8,
                maxBarThickness: 72,
              },
            ],
          },
          options: {
            layout: {
              padding: { top: 8, right: 12, bottom: 6, left: 8 },
            },
            scales: {
              x: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: '#334155',
                  font: {
                    family: 'Helvetica',
                    size: 12,
                  },
                },
              },
              y: {
                ticks: {
                  color: '#334155',
                  font: {
                    family: 'Helvetica',
                    size: 12,
                  },
                },
                beginAtZero: true,
              },
            },
            plugins: {
              title: {
                text: 'Ativos por categoria',
              },
            },
          },
        }, { width: 1280, height: 760 });
        if (categoryChart) {
          chartImages.push({ ...categoryChart, targetWidth: contentWidth, targetHeight: barChartHeight });
        }
      }

      const availableChartHeight = doc.internal.pageSize.getHeight() - margin - cursorY;

      if (isLandscape && chartImages.length >= 2) {
        const gap = 18;
        const leftChart = chartImages[0];
        const rightChart = chartImages[1];
        const slotWidth = (contentWidth - gap) / 2;

        const leftScale = Math.min(slotWidth / leftChart.targetWidth, availableChartHeight / leftChart.targetHeight);
        const rightScale = Math.min(slotWidth / rightChart.targetWidth, availableChartHeight / rightChart.targetHeight);

        leftChart.targetWidth *= leftScale;
        leftChart.targetHeight *= leftScale;
        rightChart.targetWidth *= rightScale;
        rightChart.targetHeight *= rightScale;

        const rowHeight = Math.max(leftChart.targetHeight, rightChart.targetHeight);
        const rowTop = cursorY + Math.max(0, (availableChartHeight - rowHeight) / 2);

        const leftX = margin + (slotWidth - leftChart.targetWidth) / 2;
        const rightX = margin + slotWidth + gap + (slotWidth - rightChart.targetWidth) / 2;

        doc.addImage(leftChart.image, 'PNG', leftX, rowTop, leftChart.targetWidth, leftChart.targetHeight);
        doc.addImage(rightChart.image, 'PNG', rightX, rowTop, rightChart.targetWidth, rightChart.targetHeight);
        cursorY = rowTop + rowHeight + chartSpacing;
      } else {
        const totalChartHeight = chartImages.reduce((sum, chart) => sum + chart.targetHeight, 0)
          + Math.max(0, chartImages.length - 1) * chartSpacing;

        if (totalChartHeight > availableChartHeight && totalChartHeight > 0) {
          const scale = availableChartHeight / totalChartHeight;
          chartImages.forEach((chart) => {
            chart.targetWidth *= scale;
            chart.targetHeight *= scale;
          });
        }

        chartImages.forEach((chartImage) => {
          const chartX = margin + (chartAreaWidth - chartImage.targetWidth) / 2;
          doc.addImage(
            chartImage.image,
            'PNG',
            chartX,
            cursorY,
            chartImage.targetWidth,
            chartImage.targetHeight,
          );
          cursorY += chartImage.targetHeight + chartSpacing;
        });
      }

      const maintenanceHighlights = sortedFilteredAssets
        .flatMap((asset) =>
          (asset.maintenance || [])
            .filter((entry) => entry.status !== 'COMPLETED')
            .map((entry) => ({ asset, entry })),
        )
        .sort((a, b) => {
          const dateA = new Date(a.entry.scheduledAt || a.entry.performedAt || 0).getTime();
          const dateB = new Date(b.entry.scheduledAt || b.entry.performedAt || 0).getTime();
          return dateA - dateB;
        })
        .slice(0, 5);

      doc.addPage();
      cursorY = margin;

      if (maintenanceHighlights.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(15, 23, 42);
        doc.text('Próximas intervenções de manutenção', margin, cursorY);
        cursorY += 18;

        const pageHeight = doc.internal.pageSize.getHeight();
        const tableHeaders = ['Ativo', 'Tipo', 'Status', 'Data', 'Responsável', 'Custo'];
        const maintenanceWeight = [20, 14, 14, 12, 26, 14];
        const colWidths = maintenanceWeight.map((weight) => (contentWidth * weight) / 100);
        const lineHeight = 11;
        const minRowHeight = 18;
        const cellPaddingX = 3;

        const drawMaintenanceHeader = () => {
          let x = margin;
          doc.setFillColor(11, 94, 215);
          doc.rect(margin, cursorY, contentWidth, minRowHeight, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          tableHeaders.forEach((title, idx) => {
            doc.text(title, x + cellPaddingX, cursorY + 12);
            x += colWidths[idx];
          });
          cursorY += minRowHeight;
        };

        drawMaintenanceHeader();

        maintenanceHighlights.forEach(({ asset, entry }) => {
          const date = entry.scheduledAt || entry.performedAt;
          const cells = [
            asset.tag || asset.name || '—',
            maintenanceTypeLabels[entry.type] || entry.type || '—',
            maintenanceStatusLabels[entry.status] || entry.status || '—',
            date ? formatDate(date) : '—',
            entry.responsible || '—',
            entry.cost ? formatCurrency(entry.cost) : '—',
          ];

          const wrappedCells = cells.map((text, idx) =>
            doc.splitTextToSize(String(text), Math.max(12, colWidths[idx] - cellPaddingX * 2)),
          );
          const rowHeight = Math.max(
            minRowHeight,
            ...wrappedCells.map((lines) => Math.max(1, lines.length) * lineHeight + 6),
          );

          if (cursorY + rowHeight > pageHeight - margin) {
            doc.addPage();
            cursorY = margin;
            drawMaintenanceHeader();
          }

          let x = margin;
          doc.setTextColor(51, 65, 85);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, cursorY, contentWidth, rowHeight, 'F');
          wrappedCells.forEach((lines, idx) => {
            doc.setDrawColor(226, 232, 240);
            doc.rect(x, cursorY, colWidths[idx], rowHeight);
            lines.forEach((line, lineIndex) => {
              doc.text(String(line), x + cellPaddingX, cursorY + 10 + lineIndex * lineHeight);
            });
            x += colWidths[idx];
          });

          cursorY += rowHeight;
        });
      }

      cursorY += 22;
      if (cursorY > doc.internal.pageSize.getHeight() - margin - 24) {
        doc.addPage();
        cursorY = margin;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text('Relação completa de ativos', margin, cursorY);
      cursorY += 18;

      if (!sortedFilteredAssets.length) {
        doc.text('Nenhum ativo encontrado para os filtros selecionados.', margin, cursorY);
        cursorY += 14;
      } else {
        const pageHeight = doc.internal.pageSize.getHeight();
        const tableHeaders = selectedPdfColumns.map((column) => column.label);
        const totalWeight = selectedPdfColumns.reduce((total, column) => total + column.weight, 0) || 1;
        const colWidths = selectedPdfColumns.map((column) => (contentWidth * column.weight) / totalWeight);
        const lineHeight = 11;
        const minRowHeight = 18;
        const cellPaddingX = 4;
        const drawHeader = () => {
          let x = margin;
          doc.setFillColor(11, 94, 215);
          doc.rect(margin, cursorY, contentWidth, minRowHeight, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          tableHeaders.forEach((title, idx) => {
            doc.text(title, x + cellPaddingX, cursorY + 12);
            x += colWidths[idx];
          });
          cursorY += minRowHeight;
        };
        drawHeader();

        sortedFilteredAssets.forEach((asset, rowIndex) => {
          const cells = selectedPdfColumns.map((column) => column.getValue(asset));

          const wrappedCells = cells.map((text, idx) =>
            doc.splitTextToSize(String(text), Math.max(12, colWidths[idx] - cellPaddingX * 2)),
          );
          const rowHeight = Math.max(
            minRowHeight,
            ...wrappedCells.map((lines) => Math.max(1, lines.length) * lineHeight + 6),
          );

          if (cursorY + rowHeight > pageHeight - margin) {
            doc.addPage();
            cursorY = margin;
            drawHeader();
          }

          let x = margin;
          doc.setTextColor(51, 65, 85);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 250, 252);
            doc.rect(margin, cursorY, contentWidth, rowHeight, 'F');
          }
          wrappedCells.forEach((lines, idx) => {
            doc.setDrawColor(226, 232, 240);
            doc.rect(x, cursorY, colWidths[idx], rowHeight);
            lines.forEach((line, lineIndex) => {
              doc.text(String(line), x + cellPaddingX, cursorY + 10 + lineIndex * lineHeight);
            });
            x += colWidths[idx];
          });
          cursorY += rowHeight;
        });
      }

      if (cursorY > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        cursorY = margin;
      }

      doc.setDrawColor(226, 232, 240);
      doc.line(margin, doc.internal.pageSize.getHeight() - margin, pageWidth - margin, doc.internal.pageSize.getHeight() - margin);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Emitido em ${new Date().toLocaleString('pt-BR')}`,
        margin,
        doc.internal.pageSize.getHeight() - margin + 18,
      );

      doc.save(`patrimonio-${new Date().toISOString().slice(0, 10)}.pdf`);
      showFeedback('Relatório exportado com sucesso.');
    } catch (err) {
      console.error('Erro ao exportar relatório em PDF:', err);
      handleError('Não foi possível exportar o relatório.');
    }
  }, [
    sortedFilteredAssets,
    filteredTotals,
    statusDistributionSummary,
    categoryDistributionSummary,
    selectedPdfAssetFields,
    showFeedback,
    handleError,
  ]);

  const handleDownloadImportTemplate = useCallback(() => {
    try {
      const statusOptions = STATUS_OPTIONS.map((option) => option.label);
      const categoryOptions = categories.map((category) => category.name).filter(Boolean);
      const locationOptions = locations.map((location) => location.name).filter(Boolean);

      const templateRows = [IMPORT_COLUMNS.reduce((acc, column) => ({ ...acc, [getImportColumnLabel(column)]: '' }), {})];
      const worksheet = XLSXUtils.json_to_sheet(templateRows, {
        header: IMPORT_COLUMNS.map((column) => getImportColumnLabel(column)),
        skipHeader: false,
      });
      worksheet['!cols'] = IMPORT_COLUMNS.map((column) => ({ wch: Math.max(column.label.length + 3, 20) }));
      applyNashHeaderStyle(worksheet, IMPORT_COLUMNS.map((column) => getImportColumnLabel(column)));

      const instructionsRows = IMPORT_COLUMNS.map((column) => ({
        Coluna: getImportColumnLabel(column),
        Obrigatorio: column.required ? 'Sim' : 'Não',
        Orientacao: column.instructions,
      }));
      const instructionsSheet = XLSXUtils.json_to_sheet(instructionsRows);
      instructionsSheet['!cols'] = [{ wch: 28 }, { wch: 12 }, { wch: 60 }];
      applyNashHeaderStyle(instructionsSheet, ['Coluna', 'Obrigatorio', 'Orientacao']);

      const optionsSheet = XLSXUtils.json_to_sheet([
        { Tipo: 'Status', Opcoes: statusOptions.join(' | ') || 'Sem opções cadastradas' },
        { Tipo: 'Categorias cadastradas', Opcoes: categoryOptions.join(' | ') || 'Sem categorias cadastradas' },
        { Tipo: 'Locais cadastrados', Opcoes: locationOptions.join(' | ') || 'Sem locais cadastrados' },
      ]);
      optionsSheet['!cols'] = [{ wch: 28 }, { wch: 120 }];
      applyNashHeaderStyle(optionsSheet, ['Tipo', 'Opcoes']);

      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Patrimonio');
      XLSXUtils.book_append_sheet(workbook, instructionsSheet, 'Instrucoes');
      XLSXUtils.book_append_sheet(workbook, optionsSheet, 'Opcoes');
      writeXLSXFile(workbook, 'template-importacao-patrimonio.xlsx', { bookType: 'xlsx' });
      showFeedback('Template de importação gerado com sucesso.');
    } catch (err) {
      handleError('Não foi possível gerar o template de importação.');
    }
  }, [categories, locations, handleError, showFeedback]);

  const handleImportFileChange = useCallback(
    async (event) => {
      if (!requireAssetManagementPermission()) {
        event.target.value = '';
        return;
      }
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = readXLSX(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
          throw new Error('Planilha inválida. Não foi encontrada nenhuma aba de dados.');
        }

        const rows = XLSXUtils.sheet_to_json(worksheet, { defval: '' });
        if (!rows.length) {
          setImportPreviewRows([]);
          handleError('A planilha está vazia.');
          return;
        }

        const categoryByName = new Map(categories.map((category) => [normalizeText(category.name), category]));
        const locationByName = new Map(locations.map((location) => [normalizeText(location.name), location]));
        const statusByText = STATUS_OPTIONS.reduce((acc, option) => {
          acc[normalizeText(option.value)] = option.value;
          acc[normalizeText(option.label)] = option.value;
          return acc;
        }, {});
        const existingTags = new Set(
          assets.map((asset) => normalizeText(asset.tag)).filter(Boolean),
        );
        const importTagOccurrences = new Map();

        const preview = rows.map((row, index) => {
          const normalizedRow = {};
          Object.entries(row).forEach(([header, value]) => {
            normalizedRow[normalizeHeader(header)] = value;
          });

          const payload = {};
          IMPORT_COLUMNS.forEach((column) => {
            const canonicalHeader = normalizeHeader(column.label);
            const starredHeader = normalizeHeader(getImportColumnLabel(column));
            payload[column.key] = resolveTextValue(
              normalizedRow[starredHeader] ?? normalizedRow[canonicalHeader],
            );
          });

          const errors = [];
          IMPORT_REQUIRED_FIELDS.forEach((field) => {
            if (!payload[field]) {
              const column = IMPORT_COLUMNS.find((item) => item.key === field);
              errors.push(`"${column?.label || field}" é obrigatório.`);
            }
          });

          const category = categoryByName.get(normalizeText(payload.category));
          const location = locationByName.get(normalizeText(payload.location));
          const status = statusByText[normalizeText(payload.status)] || '';

          if (payload.category && !category) errors.push(`Categoria "${payload.category}" não cadastrada.`);
          if (payload.location && !location) errors.push(`Localização "${payload.location}" não cadastrada.`);
          if (payload.status && !status) errors.push(`Status "${payload.status}" inválido.`);

          const normalizedTag = normalizeText(payload.tag);
          if (normalizedTag) {
            if (existingTags.has(normalizedTag)) {
              errors.push(`Já existe um patrimônio cadastrado com a etiqueta "${payload.tag}".`);
            }
            importTagOccurrences.set(
              normalizedTag,
              (importTagOccurrences.get(normalizedTag) || 0) + 1,
            );
          }

          return {
            rowNumber: index + 2,
            payload: {
              ...payload,
              status: status || payload.status,
              categoryId: category?.id || '',
              locationId: location?.id || '',
              acquisitionCost: parseImportNumber(payload.acquisitionCost),
              depreciationRate: parseImportNumber(payload.depreciationRate),
              usefulLifeMonths: parseImportNumber(payload.usefulLifeMonths),
              entryOrigin: resolveTextValue(payload.entryOrigin),
              invoiceNumber: resolveTextValue(payload.invoiceNumber),
              disposalDate: resolveTextValue(payload.disposalDate),
              disposalReason: resolveTextValue(payload.disposalReason),
            },
            errors,
          };
        });

        preview.forEach((item) => {
          const normalizedTag = normalizeText(item.payload.tag);
          if (normalizedTag && importTagOccurrences.get(normalizedTag) > 1) {
            item.errors.push(`A etiqueta "${item.payload.tag}" está repetida na planilha de importação.`);
          }
        });

        setImportPreviewRows(preview);
        const invalidCount = preview.filter((item) => item.errors.length > 0).length;
        if (invalidCount > 0) {
          showFeedback(`Prévia carregada com ${preview.length} linha(s). ${invalidCount} linha(s) com pendências.`, 'warning');
        } else {
          showFeedback(`Prévia carregada com ${preview.length} linha(s) pronta(s) para confirmação.`);
        }
      } catch (err) {
        handleError(err?.message || 'Não foi possível ler a planilha.');
        setImportPreviewRows([]);
      }
    },
    [assets, categories, locations, handleError, showFeedback, requireAssetManagementPermission],
  );

  const handleConfirmImport = useCallback(async () => {
    if (!requireAssetManagementPermission()) return;
    if (!importPreviewRows.length) {
      handleError('Carregue uma planilha antes de confirmar a importação.');
      return;
    }

    const invalidRows = importPreviewRows.filter((row) => row.errors.length > 0);
    if (invalidRows.length > 0) {
      const missingRequired = new Set();
      invalidRows.forEach((row) => {
        row.errors.forEach((message) => {
          if (message.includes('obrigatório')) {
            missingRequired.add(message.replace(' é obrigatório.', ''));
          }
        });
      });
      handleError(
        `Existem ${invalidRows.length} linha(s) inválida(s). Campos obrigatórios faltantes: ${
          missingRequired.size ? Array.from(missingRequired).join(', ') : 'revise a planilha'
        }.`,
      );
      return;
    }

    setImportingAssets(true);
    try {
      for (const row of importPreviewRows) {
        await AssetService.createAsset({
          name: row.payload.name,
          tag: row.payload.tag,
          serialNumber: row.payload.serialNumber,
          brand: row.payload.brand,
          status: row.payload.status,
          categoryId: row.payload.categoryId,
          locationId: row.payload.locationId,
          acquisitionDate: row.payload.acquisitionDate || '',
          acquisitionDatePrecision: getFlexibleDatePrecision(row.payload.acquisitionDate),
          acquisitionCost: row.payload.acquisitionCost ?? '',
          depreciationRate: row.payload.depreciationRate ?? '',
          usefulLifeMonths: row.payload.usefulLifeMonths ?? '',
          warrantyEnd: row.payload.warrantyEnd || '',
          custodian: row.payload.custodian,
          supplier: row.payload.supplier,
          entryOrigin: row.payload.entryOrigin,
          invoiceNumber: row.payload.invoiceNumber,
          disposalDate: row.payload.disposalDate || '',
          disposalReason: row.payload.disposalReason,
          notes: row.payload.notes,
        });
      }
      await loadAssets();
      setImportPreviewRows([]);
      showFeedback(`${importPreviewRows.length} linha(s) inserida(s) com sucesso.`);
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setImportingAssets(false);
    }
  }, [importPreviewRows, loadAssets, handleError, showFeedback, requireAssetManagementPermission]);

  const renderOverviewTab = () => {
    const statusTotal = statusDistributionSummary.values.reduce(
      (acc, value) => acc + value,
      0,
    );
    const categoryTotal = categoryDistributionSummary.values.reduce(
      (acc, value) => acc + value,
      0,
    );
    const visibleCategoryValues = categoryDistributionSummary.values.slice(0, 6);
    const visibleCategoryTotal = visibleCategoryValues.reduce((acc, value) => acc + value, 0);

    return (
      <div className="space-y-6">
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-2xl border border-blue-200 bg-blue-500/10 p-5 shadow-sm">
            <p className="text-xs font-medium text-blue-700">Ativos cadastrados</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-3xl font-semibold text-blue-950">{assets.length}</p>
              <span className="rounded-full bg-blue-500/15 p-3 text-blue-700">
                <FaClipboardList />
              </span>
            </div>
          </article>
          <article className="rounded-2xl border border-emerald-200 bg-emerald-500/10 p-5 shadow-sm">
            <p className="text-xs font-medium text-emerald-700">Valor de aquisição</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-semibold text-emerald-950">
                {formatCurrency(totalAcquisitionValue)}
              </p>
              <span className="rounded-full bg-emerald-500/15 p-3 text-emerald-700">
                <FaChartLine />
              </span>
            </div>
          </article>
          <article className="rounded-2xl border border-sky-200 bg-sky-500/10 p-5 shadow-sm">
            <p className="text-xs font-medium text-sky-700">Valor contábil estimado</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-semibold text-sky-950">
                {formatCurrency(totalCurrentValue)}
              </p>
              <span className="rounded-full bg-sky-500/15 p-3 text-sky-700">
                <FaBoxOpen />
              </span>
            </div>
          </article>
          <article className="rounded-2xl border border-amber-200 bg-amber-500/10 p-5 shadow-sm">
            <p className="text-xs font-medium text-amber-700">Alertas</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-semibold text-amber-950">
                {pendingMaintenanceCount + warrantySummary.expiring.length}
              </p>
              <span className="rounded-full bg-amber-500/15 p-3 text-amber-700">
                <FaExclamationTriangle />
              </span>
            </div>
            <p className="mt-1 text-xs text-amber-700">
              {pendingMaintenanceCount} manutenções · {warrantySummary.expiring.length} garantias
            </p>
          </article>
        </section>

        <div className="grid gap-6 xl:grid-cols-[2fr,1fr]">
          <section className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Distribuição por status</h2>
                <span className="text-xs text-slate-500">{statusTotal} ativos</span>
              </div>
              {statusTotal === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Sem dados para os filtros atuais.</p>
              ) : (
                <div className="mt-5 grid gap-5 lg:grid-cols-[220px,1fr] lg:items-center">
                  <div
                    className="mx-auto h-52 w-52 rounded-full shadow-inner ring-8 ring-white"
                    style={{ background: buildPieGradient(statusDistributionSummary.values, statusTotal) }}
                    aria-label="Gráfico de pizza da distribuição por status"
                  />
                  <ul className="space-y-3">
                    {statusDistributionSummary.labels.map((label, index) => {
                      const value = statusDistributionSummary.values[index];
                      const percentage = statusTotal ? Math.round((value / statusTotal) * 100) : 0;
                      const color = chartPalette[index % chartPalette.length];
                      return (
                        <li
                          key={label}
                          className="flex items-center justify-between rounded-xl border p-3 text-sm font-semibold shadow-sm"
                          style={{ backgroundColor: transparentizeHex(color), borderColor: transparentizeHex(color, 0.35) }}
                        >
                          <span className="flex items-center gap-2 text-slate-700">
                            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                            {label}
                          </span>
                          <span className="text-slate-800">{value} · {percentage}%</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Categorias em uso</h2>
                <span className="text-xs text-slate-500">{categoryTotal} ativos</span>
              </div>
              {categoryTotal === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Cadastre ativos para visualizar.</p>
              ) : (
                <div className="mt-5 grid gap-5 lg:grid-cols-[220px,1fr] lg:items-center">
                  <div
                    className="mx-auto h-52 w-52 rounded-full shadow-inner ring-8 ring-white"
                    style={{ background: buildPieGradient(visibleCategoryValues, visibleCategoryTotal) }}
                    aria-label="Gráfico de pizza das categorias em uso"
                  />
                  <ul className="space-y-3">
                    {categoryDistributionSummary.labels.slice(0, 6).map((label, index) => {
                      const value = categoryDistributionSummary.values[index];
                      const percentage = categoryTotal ? Math.round((value / categoryTotal) * 100) : 0;
                      const color = chartPalette[index % chartPalette.length];
                      return (
                        <li
                          key={label}
                          className="flex items-center justify-between rounded-xl border p-3 text-sm font-semibold shadow-sm"
                          style={{ backgroundColor: transparentizeHex(color), borderColor: transparentizeHex(color, 0.35) }}
                        >
                          <span className="flex items-center gap-2 text-slate-700">
                            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                            {label}
                          </span>
                          <span className="text-slate-800">{value} · {percentage}%</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Saúde dos ativos</h2>
                <span className="text-xs text-slate-500">{highlyDepreciatedAssets} críticos</span>
              </div>
              {topDepreciatedAssets.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Todos os ativos estão dentro da vida útil.</p>
              ) : (
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  {topDepreciatedAssets.slice(0, 3).map((asset) => (
                    <li
                      key={asset.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 shadow-sm"
                    >
                      <span className="truncate font-medium text-slate-700">{asset.name}</span>
                      <span className="text-sm font-semibold text-rose-600">
                        {formatPercentage(asset.depreciationPercentage)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <dl className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-500">
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Locais</dt>
                  <dd className="text-sm font-semibold text-slate-800">{locations.length}</dd>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Categorias</dt>
                  <dd className="text-sm font-semibold text-slate-800">{categories.length}</dd>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Manutenções abertas</dt>
                  <dd className="text-sm font-semibold text-slate-800">{pendingMaintenanceCount}</dd>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Garantias ativas</dt>
                  <dd className="text-sm font-semibold text-slate-800">
                    {assetsWithMetrics.filter((asset) => asset.warrantyEnd).length}
                  </dd>
                </div>
              </dl>
            </div>
          </section>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Próximas manutenções</h2>
                <button
                  type="button"
                  onClick={() => setActiveTab('maintenance')}
                  className="text-xs font-semibold text-sky-600 transition hover:text-sky-700"
                >
                  ver todas
                </button>
              </div>
              <ul className="mt-4 space-y-3">
                {upcomingMaintenance.length === 0 ? (
                  <li className="text-sm text-slate-500">Nada agendado.</li>
                ) : (
                  upcomingMaintenance.slice(0, 4).map(({ asset, entry }) => {
                    const days = daysUntil(entry.scheduledAt || entry.performedAt);
                    const badge =
                      days === null
                        ? 'bg-slate-100 text-slate-600'
                        : days < 0
                        ? 'bg-rose-100 text-rose-700'
                        : days <= 7
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700';
                    return (
                      <li key={entry.id} className="rounded-xl border border-slate-100 p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{asset.name}</p>
                            <p className="text-xs text-slate-500">
                              {MAINTENANCE_TYPE_OPTIONS.find((option) => option.value === entry.type)?.label || '—'}
                              {' · '}
                              {entry.responsible || 'Sem responsável'}
                            </p>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badge}`}>
                            {days === null
                              ? 'sem data'
                              : days < 0
                              ? `${Math.abs(days)}d atraso`
                              : `${days}d`}
                          </span>
                        </div>
                        <p className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                          <FaCalendarCheck className="text-slate-300" />
                          {entry.scheduledAt ? formatDate(entry.scheduledAt) : 'Definir data'}
                        </p>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Garantias</h2>
                <button
                  type="button"
                  onClick={() => setActiveTab('maintenance')}
                  className="text-xs font-semibold text-sky-600 transition hover:text-sky-700"
                >
                  ver detalhes
                </button>
              </div>
              <ul className="mt-4 space-y-3">
                {warrantySummary.expiring.length === 0 ? (
                  <li className="text-sm text-slate-500">Sem vencimentos nos próximos 45 dias.</li>
                ) : (
                  warrantySummary.expiring.slice(0, 4).map((asset) => (
                    <li
                      key={asset.id}
                      className="rounded-xl border border-amber-100 bg-amber-50 p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-amber-800">{asset.name}</p>
                          <p className="text-xs text-amber-700">
                            {asset.custodian || 'Responsável não informado'}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700 shadow-sm">
                          <FaCalendarTimes />
                          {formatDate(asset.warrantyEnd)}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-amber-700">Expira em {asset.days} dias</p>
                    </li>
                  ))
                )}
              </ul>
              {warrantySummary.expired.length > 0 && (
                <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {warrantySummary.expired.length} garantias vencidas
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
    );
  };

  const renderAssetsTab = () => (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Ativos</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{filteredTotals.assetCount}</p>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Valor atual</p>
          <p className="mt-1 text-lg font-bold text-slate-800">{formatCurrency(filteredTotals.totalCurrent)}</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Manutenções abertas</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{pendingMaintenanceCount}</p>
        </div>
        <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Depreciação média</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{formatPercentage(filteredTotals.averageDepreciation)}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          onClick={handleExportCsv}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar CSV
        </button>
        <button
          type="button"
          onClick={() => handleExportExcel('xls')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar XLS
        </button>
        <button
          type="button"
          onClick={() => handleExportExcel('xlsx')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar XLSX
        </button>
        <button
          type="button"
          onClick={() => setPdfOrientationModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar PDF
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Buscar</label>
            <div className="relative mt-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
              type="text"
              value={filters.search}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, search: event.target.value }))
              }
              placeholder="Nome, código ou responsável"
              className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-50"
            />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Status</label>
            <select
              value={filters.status}
              onChange={(event) => {
                const nextStatus = event.target.value;
                setFilters((prev) => ({
                  ...prev,
                  status: nextStatus,
                  disposalDate: nextStatus === 'DISPOSED' ? prev.disposalDate : '',
                  disposalReason: nextStatus === 'DISPOSED' ? prev.disposalReason : '',
                }));
              }}
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">Todos</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {filters.status === 'DISPOSED' && (
            <>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-slate-500">Data de baixa</label>
                <input
                  type="date"
                  value={filters.disposalDate}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, disposalDate: event.target.value }))
                  }
                  className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-slate-500">Motivo da baixa</label>
                <input
                  type="text"
                  value={filters.disposalReason}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, disposalReason: event.target.value }))
                  }
                  className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>
            </>
          )}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Categoria</label>
            <select
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, categoryId: event.target.value }))
              }
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">Todas</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Localização</label>
            <select
              value={filters.locationId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, locationId: event.target.value }))
              }
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">Todas</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Origem de entrada</label>
            <input
              type="text"
              value={filters.entryOrigin}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, entryOrigin: event.target.value }))
              }
              placeholder="Compra, doação..."
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Fornecedor</label>
            <input
              type="text"
              value={filters.supplier}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, supplier: event.target.value }))
              }
              placeholder="Nome do fornecedor"
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Data de aquisição</label>
            <input
              type="text"
              value={filters.acquisitionDate}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, acquisitionDate: event.target.value }))
              }
              placeholder="AAAA, MM/AAAA ou DD/MM/AAAA"
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Observações</label>
            <input
              type="text"
              value={filters.notes}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, notes: event.target.value }))
              }
              placeholder="Texto nas observações"
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
        </div>
        {canManage && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openCategoryModal(null)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaCogs />
              Nova categoria
            </button>
            <button
              type="button"
              onClick={() => openLocationModal(null)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaMapMarkedAlt />
              Novo local
            </button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-[920px] divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Ativo</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-left">Local</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Aquisição</th>
                <th className="px-4 py-3 text-left">Origem da entrada</th>
                <th className="px-4 py-3 text-left">Responsável</th>
                {canManage && <th className="px-4 py-3 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={canManage ? 8 : 7}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Carregando ativos...
                  </td>
                </tr>
              ) : sortedFilteredAssets.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManage ? 8 : 7}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Nenhum ativo encontrado.
                  </td>
                </tr>
              ) : (
                paginatedAssets.map((asset) => (
                  <tr key={asset.id} className="transition hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-800">{asset.name}</div>
                      <div className="text-xs text-slate-400">{asset.tag || asset.serialNumber || '—'}</div>
                      {Array.isArray(asset.attachments) && asset.attachments.length > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-sky-600">
                          <FaPaperclip />
                          {asset.attachments.length} anexo(s)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.category?.name || '—'}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.location?.name || '—'}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
                          statusBadgeStyles[asset.status] || 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}
                      >
                        {statusLabels[asset.status] || asset.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-slate-600">
                      {formatCurrency(asset.acquisitionCost)}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.entryOrigin || '—'}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.custodian || '—'}</td>
                    {canManage && (
                      <td className="px-4 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openMaintenanceModal(asset)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FaTools />
                            Manutenção
                          </button>
                          <button
                            type="button"
                            onClick={() => openAssetModal(asset)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FaEdit />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAsset(asset)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                          >
                            <FaTrash />
                            Excluir
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div
          className="asset-pagination mt-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between"
          aria-label="Paginação da consulta de patrimônios"
        >
          <span aria-live="polite">
            Exibindo {assetPagination.start}–{assetPagination.end} de {assetPagination.total} patrimônio(s)
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="asset-page-size" className="text-xs font-medium text-slate-500">
              Itens por página
            </label>
            <select
              id="asset-page-size"
              value={assetPageSize}
              onChange={(event) => setAssetPageSize(Number(event.target.value))}
              className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setAssetPage(assetPagination.page - 1)}
              disabled={assetPagination.page === 1}
              className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="min-w-24 text-center">
              Página {assetPagination.page} de {assetPagination.totalPages}
            </span>
            <button
              type="button"
              onClick={() => setAssetPage(assetPagination.page + 1)}
              disabled={assetPagination.page === assetPagination.totalPages}
              className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderImportTab = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">Importação via planilha (Excel)</h2>
        <p className="mt-1 text-sm text-slate-500">
          Siga os passos abaixo para importar ativos de patrimônio com validação de campos obrigatórios.
        </p>
        <ol className="mt-4 space-y-2 text-sm text-slate-600">
          <li><strong>1.</strong> Baixe o template padrão e preencha os dados (colunas com <strong>*</strong> são obrigatórias).</li>
          <li><strong>2.</strong> Use as opções da aba <strong>Opcoes</strong> para Status, Categorias e Locais.</li>
          <li><strong>3.</strong> Envie o arquivo para carregar a prévia.</li>
          <li><strong>4.</strong> Confira os dados na tabela abaixo e confirme a importação.</li>
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleDownloadImportTemplate}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FaDownload />
            Baixar template
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700">
            <FaFileImport />
            Selecionar planilha
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFileChange} />
          </label>
          <button
            type="button"
            onClick={handleConfirmImport}
            disabled={!importPreviewRows.length || importingAssets}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {importingAssets ? 'Importando…' : 'Confirmar importação'}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Prévia antes de inserir</h3>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
            {importPreviewRows.length} linha(s)
          </span>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-[980px] divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Linha</th>
                <th className="px-4 py-3 text-left">Ativo</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-left">Localização</th>
                <th className="px-4 py-3 text-left">Pendências</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {importPreviewRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                    Nenhuma planilha carregada para prévia.
                  </td>
                </tr>
              ) : (
                importPreviewRows.map((row) => (
                  <tr key={row.rowNumber} className={row.errors.length ? 'bg-rose-50/60' : ''}>
                    <td className="px-4 py-3 text-slate-600">{row.rowNumber}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.status || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.category || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.location || '—'}</td>
                    <td className="px-4 py-3 text-xs text-rose-700">
                      {row.errors.length ? row.errors.join(' ') : 'Sem pendências'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderMaintenanceTab = () => (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><FaCalendarCheck /></span>
            <span className="text-2xl font-bold text-slate-800">{upcomingMaintenance.length}</span>
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">Programadas</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><FaClock /></span>
            <span className="text-2xl font-bold text-slate-800">
              {upcomingMaintenance.filter(({ entry }) => {
                const days = daysUntil(entry.scheduledAt || entry.performedAt);
                return days !== null && days <= 7;
              }).length}
            </span>
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">Próximas ou atrasadas</p>
        </div>
        <div className="rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700"><FaRedoAlt /></span>
            <span className="text-2xl font-bold text-slate-800">
              {maintenanceHistory.filter(({ entry }) => Boolean(entry.recurring || entry.periodicityValue)).length}
            </span>
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">Recorrentes</p>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><FaCheckCircle /></span>
            <span className="text-2xl font-bold text-slate-800">
              {maintenanceHistory.filter(({ entry }) => entry.status === 'COMPLETED').length}
            </span>
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">Concluídas recentes</p>
        </div>
      </div>

      {canManage && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => openMaintenanceModal(null)}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62]"
          >
            <FaPlus />
            Registrar manutenção
          </button>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.35fr_.65fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FaTools /></span>
              <h2 className="text-lg font-semibold text-slate-800">Próximas manutenções</h2>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
              {upcomingMaintenance.length}
            </span>
          </div>
          <ul className="mt-4 space-y-3">
            {upcomingMaintenance.length === 0 ? (
              <li className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Nenhuma manutenção programada.
              </li>
            ) : (
              upcomingMaintenance.slice(0, 12).map(({ asset, entry }) => {
                const days = daysUntil(entry.scheduledAt || entry.performedAt);
                const badge =
                  days === null
                    ? 'bg-slate-100 text-slate-600'
                    : days < 0
                    ? 'bg-rose-100 text-rose-700'
                    : days <= 7
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-emerald-100 text-emerald-700';
                return (
                  <li key={entry.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-800">{asset.name}</p>
                          {(entry.recurring || entry.periodicityValue) && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700">
                              <FaRedoAlt />
                              A cada {entry.periodicityValue} {MAINTENANCE_PERIODICITY_UNIT_OPTIONS.find((option) => option.value === entry.periodicityUnit)?.label?.toLowerCase() || 'período'}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {MAINTENANCE_TYPE_OPTIONS.find((option) => option.value === entry.type)?.label || '—'} · {entry.responsible || 'Sem responsável'}
                        </p>
                        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500">
                          <FaCalendarCheck className="text-blue-500" />
                          {entry.scheduledAt ? formatDate(entry.scheduledAt) : 'Sem data definida'}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badge}`}>
                          {days === null ? 'Sem data' : days < 0 ? `${Math.abs(days)} dias atraso` : days === 0 ? 'Hoje' : `${days} dias`}
                        </span>
                        {canManage && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setMaintenanceForm({
                                  id: entry.id,
                                  assetId: asset.id,
                                  type: entry.type,
                                  status: entry.status,
                                  recurring: Boolean(entry.recurring || entry.periodicityValue),
                                  periodicityValue: entry.periodicityValue ? String(entry.periodicityValue) : '',
                                  periodicityUnit: entry.periodicityUnit || 'MONTHS',
                                  scheduledAt: toInputDateTime(entry.scheduledAt),
                                  performedAt: toInputDateTime(entry.performedAt),
                                  responsible: entry.responsible || '',
                                  cost: entry.cost ? String(entry.cost) : '',
                                  notes: entry.notes || '',
                                });
                                setMaintenanceModalOpen(true);
                              }}
                              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
                              title="Editar manutenção"
                            >
                              <FaEdit />
                            </button>
                            {entry.status !== 'COMPLETED' && (
                              <button
                                type="button"
                                onClick={() => handleCompleteMaintenance(asset.id, entry)}
                                className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                              >
                                <FaCheckCircle />
                                Concluir
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><FaShieldAlt /></span>
            <h2 className="text-lg font-semibold text-slate-800">Garantias</h2>
          </div>
          <div className="mt-4 space-y-3">
            {warrantySummary.expiring.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Sem vencimentos em 45 dias.
              </div>
            ) : (
              warrantySummary.expiring.slice(0, 8).map((asset) => (
                <div key={asset.id} className="rounded-xl border border-amber-100 bg-amber-50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-amber-900">{asset.name}</p>
                      <p className="text-xs text-amber-700">{asset.custodian || 'Sem responsável'}</p>
                    </div>
                    <span className="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-amber-700">
                      {asset.days} dias
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-amber-700">{formatDate(asset.warrantyEnd)}</p>
                </div>
              ))
            )}
          </div>
          {warrantySummary.expired.length > 0 && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
              <strong>{warrantySummary.expired.length}</strong> garantia(s) vencida(s).
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><FaClipboardList /></span>
            <h2 className="text-lg font-semibold text-slate-800">Histórico</h2>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">{maintenanceHistory.length} registros</span>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-[820px] divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Ativo</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Recorrência</th>
                <th className="px-4 py-3 text-left">Responsável</th>
                <th className="px-4 py-3 text-right">Data</th>
                <th className="px-4 py-3 text-right">Custo</th>
                {canManage && <th className="px-4 py-3 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {maintenanceHistory.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 8 : 7} className="px-4 py-8 text-center text-slate-500">Nenhuma manutenção registrada.</td>
                </tr>
              ) : (
                maintenanceHistory.map(({ asset, entry }) => (
                  <tr key={entry.id} className="transition hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <p className="font-semibold text-slate-800">{asset.name}</p>
                      <p className="text-xs text-slate-400">{asset.tag || asset.serialNumber || '—'}</p>
                    </td>
                    <td className="px-4 py-4 text-slate-600">{maintenanceTypeLabels[entry.type] || entry.type || '—'}</td>
                    <td className="px-4 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        entry.status === 'COMPLETED'
                          ? 'bg-emerald-50 text-emerald-700'
                          : entry.status === 'IN_PROGRESS'
                          ? 'bg-blue-50 text-blue-700'
                          : entry.status === 'CANCELLED'
                          ? 'bg-slate-100 text-slate-600'
                          : 'bg-amber-50 text-amber-700'
                      }`}>
                        {maintenanceStatusLabels[entry.status] || entry.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-600">
                      {(entry.recurring || entry.periodicityValue)
                        ? `A cada ${entry.periodicityValue} ${MAINTENANCE_PERIODICITY_UNIT_OPTIONS.find((option) => option.value === entry.periodicityUnit)?.label?.toLowerCase() || ''}`
                        : 'Única'}
                    </td>
                    <td className="px-4 py-4 text-slate-600">{entry.responsible || '—'}</td>
                    <td className="px-4 py-4 text-right text-slate-600">{entry.performedAt ? formatDate(entry.performedAt) : formatDate(entry.scheduledAt)}</td>
                    <td className="px-4 py-4 text-right text-slate-600">{entry.cost ? formatCurrency(entry.cost) : '—'}</td>
                    {canManage && (
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setMaintenanceForm({
                                id: entry.id,
                                assetId: asset.id,
                                type: entry.type,
                                status: entry.status,
                                recurring: Boolean(entry.recurring || entry.periodicityValue),
                                periodicityValue: entry.periodicityValue ? String(entry.periodicityValue) : '',
                                periodicityUnit: entry.periodicityUnit || 'MONTHS',
                                scheduledAt: toInputDateTime(entry.scheduledAt),
                                performedAt: toInputDateTime(entry.performedAt),
                                responsible: entry.responsible || '',
                                cost: entry.cost ? String(entry.cost) : '',
                                notes: entry.notes || '',
                              });
                              setMaintenanceModalOpen(true);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMaintenance(asset.id, entry)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                            title="Excluir"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  const renderDirectoriesTab = () => (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><FaLayerGroup /></span>
            <div><p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Categorias</p><p className="text-2xl font-bold text-slate-900">{categories.length}</p></div>
          </div>
        </div>
        <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700"><FaMapMarkedAlt /></span>
            <div><p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Locais</p><p className="text-2xl font-bold text-slate-900">{locations.length}</p></div>
          </div>
        </div>
      </div>
      {canManage && (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600"><FaBell /></span>
              <h2 className="text-lg font-semibold text-slate-800">Lembretes padrão</h2>
            </div>
            <button
              type="button"
              onClick={handleSaveReminderDefaults}
              disabled={savingReminderDefaults}
              className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaCheckCircle />
              {savingReminderDefaults ? 'Salvando...' : 'Salvar padrão'}
            </button>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Usuários padrão</label>
              <select
                multiple
                value={reminderDefaults.userIds || []}
                onChange={(event) => handleDefaultReminderSelectionChange('userIds', event.target.selectedOptions)}
                className="mt-1 min-h-[8rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
              >
                {calendarRecipients.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}{user.email ? ` (${user.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Setores padrão</label>
              <select
                multiple
                value={reminderDefaults.sectorIds || []}
                onChange={(event) => handleDefaultReminderSelectionChange('sectorIds', event.target.selectedOptions)}
                className="mt-1 min-h-[8rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
              >
                {calendarRecipients.sectors.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Categorias</h2>
          {canManage && (
            <button
              type="button"
              onClick={() => openCategoryModal(null)}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
            >
              <FaPlus />
              Nova categoria
            </button>
          )}
        </div>
        <ul className="mt-4 space-y-3 text-sm text-slate-600">
          {categories.length === 0 ? (
            <li className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              Nenhuma categoria cadastrada.
            </li>
          ) : (
            categories.map((category) => {
              const count = assets.filter((asset) => asset.category?.id === category.id).length;
              return (
                <li
                  key={category.id}
                  className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="flex items-center gap-2 font-semibold text-slate-800">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: category.color || '#0ea5e9' }}
                        />
                        {category.name}
                      </p>
                      {category.description && (
                        <p className="mt-1 text-xs text-slate-500">{category.description}</p>
                      )}
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      {count} ativo(s)
                    </span>
                  </div>
                  {canManage && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openCategoryModal(category)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FaEdit />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(category)}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                      >
                        <FaTrash />
                        Excluir
                      </button>
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Locais</h2>
          {canManage && (
            <button
              type="button"
              onClick={() => openLocationModal(null)}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
            >
              <FaPlus />
              Novo local
            </button>
          )}
        </div>
        <ul className="mt-4 space-y-3 text-sm text-slate-600">
          {locations.length === 0 ? (
            <li className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              Nenhum local cadastrado.
            </li>
          ) : (
            locations.map((location) => {
              const count = assets.filter((asset) => asset.location?.id === location.id).length;
              return (
                <li
                  key={location.id}
                  className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800">{location.name}</p>
                      {location.description && (
                        <p className="mt-1 text-xs text-slate-500">{location.description}</p>
                      )}
                      {(location.responsible || location.contact) && (
                        <p className="mt-2 text-xs text-slate-500">
                          {location.responsible && <span>Responsável: {location.responsible}</span>}
                          {location.responsible && location.contact && ' · '}
                          {location.contact && <span>Contato: {location.contact}</span>}
                        </p>
                      )}
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      {count} ativo(s)
                    </span>
                  </div>
                  {canManage && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openLocationModal(location)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FaEdit />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteLocation(location)}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                      >
                        <FaTrash />
                        Excluir
                      </button>
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>
      </div>
    </div>
  );

  if (!canView) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <FaUserShield className="mx-auto mb-4 h-12 w-12 text-slate-400" />
          <h2 className="text-xl font-semibold text-slate-800">
            Você não possui acesso ao módulo de patrimônio.
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Entre em contato com o administrador para solicitar as permissões necessárias.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:space-y-6 sm:p-6">
      <header className="flex flex-col gap-4 rounded-2xl bg-[#071f54] p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-xl text-white shadow-inner">
            <FaBoxOpen />
          </span>
          <h1 className="text-2xl font-semibold text-white">Controle de Patrimônio</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => openAssetModal(null)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#071f54] shadow-sm transition hover:bg-slate-100"
          >
            <FaPlus />
            Novo ativo
          </button>
        )}
      </header>

      {loading && <AssetLoadingBar />}

      {!canManage && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Você pode visualizar o controle de patrimônio, mas não tem permissão para alterar, excluir ou adicionar ativos, categorias ou locais.
        </div>
      )}

      {(feedback || (error && !assetModalOpen)) && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-sm"
          role="alertdialog"
          aria-live="assertive"
          aria-modal="true"
          aria-labelledby="asset-feedback-title"
        >
          <div className="w-full max-w-md overflow-x-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10 max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
              <div>
                <p id="asset-feedback-title" className="text-base font-semibold text-slate-900">
                  Mensagem do patrimônio
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFeedback('');
                  setError('');
                }}
                className="rounded-full p-1 text-xl leading-none text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar mensagem"
              >
                ×
              </button>
            </div>

            <div className="space-y-3 p-5">
              {feedback && (
                <div
                  className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
                    feedbackType === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-amber-200 bg-amber-50 text-amber-800'
                  }`}
                >
                  {feedbackType === 'success' ? (
                    <FaCheckCircle className="mt-0.5 shrink-0" />
                  ) : (
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  )}
                  <span>{feedback}</span>
                </div>
              )}
              {error && !assetModalOpen && (
                <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="inline-flex min-w-full gap-2 rounded-2xl border border-slate-200 bg-white p-1">
          {TAB_ITEMS.filter((tab) => canManage || tab.id !== 'import').map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                  isActive ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Icon />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'ai' && (
          <AIInsightsPanel
            module="assets"
            title="Insights inteligentes do patrimônio"
            subtitle="Análise executiva do patrimônio, cruzando valores, depreciação, manutenção, garantias, status, categorias e locais."
            data={{ assets, categories, locations }}
          />
        )}
        {activeTab === 'assets' && renderAssetsTab()}
        {canManage && activeTab === 'import' && renderImportTab()}
        {activeTab === 'maintenance' && renderMaintenanceTab()}
        {activeTab === 'directories' && renderDirectoriesTab()}
      </div>
      <Modal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        size="max-w-5xl"
        showCloseButton={false}
      >
        <div className="overflow-hidden rounded-2xl bg-slate-50">
          <div className="flex items-start justify-between gap-4 bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] px-5 py-5 text-white sm:px-6">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 text-xl shadow-inner">
                <FaBoxOpen />
              </span>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold">
                  {assetForm.id ? 'Editar ativo' : 'Cadastrar novo ativo'}
                </h2>
                
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAssetModalOpen(false)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fechar cadastro do ativo"
            >
              <FaTimes />
            </button>
          </div>

          <div className="space-y-5 p-4 sm:p-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <FaTag />
                </span>
                <div>
                  <h3 className="font-semibold text-slate-800">Identificação do ativo</h3>
                  
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2">
                  <span className="flex items-center gap-2">
                    <FaBoxOpen className="text-slate-400" />
                    Nome do ativo <span className="text-rose-500">*</span>
                  </span>
                  <input
                    type="text"
                    value={assetForm.name}
                    onChange={(event) => setAssetForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="Ex.: Notebook Dell Latitude"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2">
                    <FaTag className="text-slate-400" />
                    Etiqueta interna
                  </span>
                  <input
                    type="text"
                    value={assetForm.tag}
                    onChange={(event) => setAssetForm((prev) => ({ ...prev, tag: event.target.value }))}
                    placeholder="Ex.: PAT-000123"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2">
                    <FaBarcode className="text-slate-400" />
                    Número de série / patrimônio
                  </span>
                  <input
                    type="text"
                    value={assetForm.serialNumber}
                    onChange={(event) => setAssetForm((prev) => ({ ...prev, serialNumber: event.target.value }))}
                    placeholder="Número de série do fabricante"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2">
                    <FaIndustry className="text-slate-400" />
                    Marca
                  </span>
                  <input
                    type="text"
                    value={assetForm.brand}
                    onChange={(event) => setAssetForm((prev) => ({ ...prev, brand: event.target.value }))}
                    placeholder="Ex.: Dell, Samsung, Tramontina..."
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-1">
                    Categoria <span className="text-rose-500">*</span>
                  </span>
                  <select
                    value={assetForm.categoryId}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, categoryId: event.target.value }))
                    }
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2">
                    <FaMapMarkedAlt className="text-slate-400" />
                    Localização <span className="text-rose-500">*</span>
                  </span>
                  <select
                    value={assetForm.locationId}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, locationId: event.target.value }))
                    }
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                  >
                    <option value="">Selecione uma localização</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-5">
                <p className="mb-2 text-sm font-semibold text-slate-700">Status operacional <span className="text-rose-500">*</span></p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {STATUS_OPTIONS.map((option) => {
                    const meta = ASSET_STATUS_META[option.value] || ASSET_STATUS_META.STOCK;
                    const StatusIcon = meta.icon;
                    const selected = assetForm.status === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          const nextStatus = option.value;
                          setAssetForm((prev) => ({
                            ...prev,
                            status: nextStatus,
                            disposalDate: nextStatus === 'DISPOSED' ? prev.disposalDate : '',
                            disposalReason: nextStatus === 'DISPOSED' ? prev.disposalReason : '',
                          }));
                        }}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left text-sm font-semibold transition ${
                          selected
                            ? meta.activeClass
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                        aria-pressed={selected}
                      >
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.iconClass}`}>
                          <StatusIcon />
                        </span>
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              {assetForm.status === 'DISPOSED' && (
                <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                    <FaTrash />
                  </span>
                  <div>
                    <h3 className="font-semibold text-rose-900">Baixa patrimonial</h3>
                    <p className="text-xs text-rose-700">Informe os dados da retirada deste ativo do patrimônio.</p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                    Data de baixa
                    <input
                      type="date"
                      value={assetForm.disposalDate}
                      onChange={(event) =>
                        setAssetForm((prev) => ({ ...prev, disposalDate: event.target.value }))
                      }
                      className="rounded-xl border border-rose-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                    Motivo da baixa
                    <input
                      type="text"
                      value={assetForm.disposalReason}
                      onChange={(event) =>
                        setAssetForm((prev) => ({ ...prev, disposalReason: event.target.value }))
                      }
                      placeholder="Ex.: Inservível, doação, descarte..."
                      className="rounded-xl border border-rose-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                    />
                  </label>
                </div>
                </div>
              )}

              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <FaDollarSign />
                </span>
                <div>
                  <h3 className="font-semibold text-slate-800">Aquisição e informações financeiras</h3>
                  
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaCalendarAlt className="text-slate-400" /> Data de aquisição</span>
                  <input
                    type="text"
                    value={assetForm.acquisitionDate}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, acquisitionDate: event.target.value }))
                    }
                    placeholder="AAAA, MM/AAAA ou DD/MM/AAAA"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                  <span className="text-xs font-normal text-slate-400">Ano, mês/ano ou data completa.</span>
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaDollarSign className="text-slate-400" /> Valor de aquisição</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={assetForm.acquisitionCost}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, acquisitionCost: event.target.value }))
                    }
                    placeholder="0,00"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Taxa de depreciação anual (%)
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={assetForm.depreciationRate}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, depreciationRate: event.target.value }))
                    }
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Vida útil (meses)
                  <input
                    type="number"
                    min="0"
                    value={assetForm.usefulLifeMonths || ''}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, usefulLifeMonths: event.target.value }))
                    }
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaShieldAlt className="text-slate-400" /> Garantia até</span>
                  <input
                    type="date"
                    value={assetForm.warrantyEnd}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, warrantyEnd: event.target.value }))
                    }
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaBuilding className="text-slate-400" /> Fornecedor</span>
                  <input
                    type="text"
                    value={assetForm.supplier}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, supplier: event.target.value }))
                    }
                    placeholder="Fornecedor do ativo"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Origem da entrada
                  <input
                    type="text"
                    value={assetForm.entryOrigin}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, entryOrigin: event.target.value }))
                    }
                    placeholder="Ex.: Compra, doação, transferência"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaFileInvoiceDollar className="text-slate-400" /> N° da Nota Fiscal</span>
                  <input
                    type="text"
                    value={assetForm.invoiceNumber}
                    onChange={(event) =>
                      setAssetForm((prev) => ({ ...prev, invoiceNumber: event.target.value }))
                    }
                    placeholder="Número da NF"
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                  />
                </label>
              </div>
            </section>


            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <FaStickyNote />
                </span>
                <div>
                  <h3 className="font-semibold text-slate-800">Observações e anexos</h3>
                  
                </div>
              </div>

              <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                Observações e histórico relevante
                <textarea
                  value={assetForm.notes}
                  onChange={(event) =>
                    setAssetForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  rows={4}
                  placeholder="Descreva informações importantes, histórico de uso ou observações..."
                  className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-50"
                />
              </label>

              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-sky-600 shadow-sm">
                    <FaPaperclip />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Anexos do ativo</p>
                    
                  </div>
                </div>
                <input
                  type="file"
                  multiple
                  onChange={(event) => {
                    handleAddAssetAttachments(event.target.files);
                    event.target.value = '';
                  }}
                  className="mt-3 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-sky-50 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-sky-700 hover:file:bg-sky-100"
                />

                <ul className="mt-3 space-y-2">
                  {(assetForm.attachments || []).length === 0 ? (
                    <li className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-3 text-center text-xs text-slate-500">
                      Nenhum anexo incluído para este ativo.
                    </li>
                  ) : (
                    (assetForm.attachments || []).map((attachment) => (
                      <li
                        key={attachment.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
                            <FaPaperclip />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-700">{attachment.name}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(attachment.size)} • {formatDate(attachment.uploadedAt)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenAssetAttachment(attachment)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-sky-300 hover:text-sky-700"
                          >
                            Abrir
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveAssetAttachment(attachment.id)}
                            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                          >
                            Remover
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </section>

            <details className="group rounded-2xl border border-violet-200 bg-white shadow-sm">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl px-4 py-4 transition hover:bg-violet-50/60 sm:px-5 [&::-webkit-details-marker]:hidden">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                    <FaBell />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800">Lembretes</h3>
                    
                  </div>
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-violet-100 bg-violet-50 text-violet-600 transition group-open:rotate-180">
                  <FaChevronDown />
                </span>
              </summary>

              <div className="border-t border-violet-100 px-4 pb-5 pt-4 sm:px-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2">
                    <span className="flex items-center gap-2">
                      <FaUser className="text-slate-400" />
                      Responsável pelo ativo
                    </span>
                    <input
                      type="text"
                      value={assetForm.custodian}
                      onChange={(event) =>
                        setAssetForm((prev) => ({ ...prev, custodian: event.target.value }))
                      }
                      placeholder="Pessoa ou área responsável"
                      className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-violet-400 focus:ring-4 focus:ring-violet-50"
                    />
                  </label>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                      <span className="flex items-center gap-2">
                        <FaUser className="text-violet-500" />
                        Usuários para lembretes
                      </span>
                      <select
                        multiple
                        value={assetForm.responsibleUserIds || []}
                        onChange={(event) =>
                          handleReminderSelectionChange('responsibleUserIds', event.target.selectedOptions)
                        }
                        className="min-h-[7rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-violet-400 focus:ring-4 focus:ring-violet-50"
                      >
                        {calendarRecipients.users.map((recipient) => (
                          <option key={recipient.id} value={recipient.id}>
                            {recipient.name}{recipient.email ? ` (${recipient.email})` : ''}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs font-normal text-slate-500">
                        Ctrl/Cmd permite selecionar mais de um usuário.
                      </span>
                    </label>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                      <span className="flex items-center gap-2">
                        <FaUsers className="text-violet-500" />
                        Setores para lembretes
                      </span>
                      <select
                        multiple
                        value={assetForm.responsibleSectorIds || []}
                        onChange={(event) =>
                          handleReminderSelectionChange('responsibleSectorIds', event.target.selectedOptions)
                        }
                        className="min-h-[7rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-violet-400 focus:ring-4 focus:ring-violet-50"
                      >
                        {calendarRecipients.sectors.map((sector) => (
                          <option key={sector.id} value={sector.id}>
                            {sector.name}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs font-normal text-slate-500">
                        Os usuários dos setores selecionados receberão os lembretes.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </details>

            <div className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 border-t border-slate-200 bg-white/95 px-4 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] backdrop-blur sm:-mx-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              {error ? (
                <div className="flex max-w-xl items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  Campos com <span className="font-semibold text-rose-500">*</span> são obrigatórios.
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAssetModalOpen(false)}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveAsset}
                  disabled={savingAsset}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0b3474] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaCheckCircle />
                  {savingAsset ? 'Salvando...' : assetForm.id ? 'Salvar alterações' : 'Cadastrar ativo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={pdfOrientationModalOpen}
        onClose={() => setPdfOrientationModalOpen(false)}
        size="max-w-md"
      >
        <div className="space-y-4 p-6">
          <h3 className="text-lg font-semibold text-slate-800">Orientação do PDF</h3>
          <p className="text-sm text-slate-600">
            Escolha os campos da relação de ativos que devem aparecer no PDF e a orientação do arquivo.
          </p>
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Campos do PDF</span>
              <button
                type="button"
                onClick={() => setSelectedPdfAssetFields(PDF_ASSET_COLUMNS.map((column) => column.key))}
                className="text-xs font-semibold text-sky-600 hover:text-sky-700"
              >
                Marcar todos
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {PDF_ASSET_COLUMNS.map((column) => (
                <label key={column.key} className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={selectedPdfAssetFields.includes(column.key)}
                    onChange={(event) => {
                      setSelectedPdfAssetFields((prev) => (
                        event.target.checked
                          ? Array.from(new Set([...prev, column.key]))
                          : prev.filter((field) => field !== column.key)
                      ));
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  {column.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                setPdfOrientationModalOpen(false);
                handleExportPdf('portrait');
              }}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Retrato
            </button>
            <button
              type="button"
              onClick={() => {
                setPdfOrientationModalOpen(false);
                handleExportPdf('landscape');
              }}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Paisagem
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={maintenanceModalOpen}
        onClose={() => setMaintenanceModalOpen(false)}
        size="max-w-4xl"
        showCloseButton={false}
      >
        <div className="overflow-hidden rounded-2xl bg-slate-50">
          <div className="flex items-center justify-between bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] px-5 py-5 text-white sm:px-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-xl"><FaTools /></span>
              <h2 className="text-xl font-semibold">{maintenanceForm.id ? 'Editar manutenção' : 'Registrar manutenção'}</h2>
            </div>
            <button type="button" onClick={() => setMaintenanceModalOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 hover:bg-white/20" aria-label="Fechar">
              <FaTimes />
            </button>
          </div>

          <div className="space-y-5 p-4 sm:p-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2">
                  <span className="flex items-center gap-2"><FaBoxOpen className="text-blue-500" /> Ativo <span className="text-rose-500">*</span></span>
                  <select
                    value={maintenanceForm.assetId || ''}
                    onChange={(event) => {
                      const assetId = event.target.value;
                      const asset = assets.find((candidate) => String(candidate.id) === String(assetId));
                      setMaintenanceForm((prev) => ({
                        ...prev,
                        assetId,
                        responsible: prev.responsible || asset?.custodian || '',
                      }));
                    }}
                    disabled={Boolean(maintenanceForm.id)}
                    className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-100"
                  >
                    <option value="">Selecione o ativo</option>
                    {assets.slice().sort((a,b)=>String(a.name||'').localeCompare(String(b.name||''),'pt-BR')).map((asset) => (
                      <option key={asset.id} value={asset.id}>{asset.name}{asset.tag ? ` · ${asset.tag}` : ''}</option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Tipo de manutenção
                  <select value={maintenanceForm.type} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, type: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50">
                    {MAINTENANCE_TYPE_OPTIONS.map((option)=><option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Status
                  <select value={maintenanceForm.status} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, status: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50">
                    {MAINTENANCE_STATUS_OPTIONS.map((option)=><option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaCalendarCheck className="text-blue-500" /> Agendamento</span>
                  <input type="datetime-local" value={maintenanceForm.scheduledAt} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, scheduledAt: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>

                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Conclusão
                  <input type="datetime-local" value={maintenanceForm.performedAt} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, performedAt: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>
              </div>
            </section>

            <section className={`rounded-2xl border p-4 shadow-sm transition sm:p-5 ${maintenanceForm.recurring ? 'border-violet-200 bg-violet-50/60' : 'border-slate-200 bg-white'}`}>
              <button
                type="button"
                onClick={() => setMaintenanceForm((prev) => ({ ...prev, recurring: !prev.recurring, periodicityValue: prev.recurring ? '' : (prev.periodicityValue || '1') }))}
                className="flex w-full items-center justify-between gap-4 text-left"
              >
                <span className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${maintenanceForm.recurring ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}><FaRedoAlt /></span>
                  <span>
                    <strong className="block text-sm text-slate-800">Manutenção recorrente</strong>
                    <span className="text-xs text-slate-500">Ao concluir, o próximo agendamento é criado automaticamente.</span>
                  </span>
                </span>
                <span className={`relative h-6 w-11 rounded-full transition ${maintenanceForm.recurring ? 'bg-violet-600' : 'bg-slate-300'}`}>
                  <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${maintenanceForm.recurring ? 'left-6' : 'left-1'}`} />
                </span>
              </button>
              {maintenanceForm.recurring && (
                <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr]">
                  <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                    Repetir a cada
                    <input type="number" min="1" value={maintenanceForm.periodicityValue} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, periodicityValue: event.target.value }))} className="rounded-xl border border-violet-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100" />
                  </label>
                  <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                    Período
                    <select value={maintenanceForm.periodicityUnit} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, periodicityUnit: event.target.value }))} className="rounded-xl border border-violet-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100">
                      {MAINTENANCE_PERIODICITY_UNIT_OPTIONS.map((option)=><option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaUser className="text-slate-400" /> Responsável</span>
                  <input type="text" value={maintenanceForm.responsible} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, responsible: event.target.value }))} placeholder="Pessoa, equipe ou fornecedor" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-2"><FaDollarSign className="text-slate-400" /> Custo (R$)</span>
                  <input type="number" min="0" step="0.01" value={maintenanceForm.cost} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, cost: event.target.value }))} placeholder="0,00" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2">
                  Observações
                  <textarea rows={4} value={maintenanceForm.notes} onChange={(event) => setMaintenanceForm((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Serviços previstos, peças, orientações..." className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>
              </div>
            </section>

            {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
              <button type="button" onClick={() => setMaintenanceModalOpen(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button type="button" onClick={handleSaveMaintenance} disabled={savingMaintenance || !maintenanceForm.assetId || (maintenanceForm.recurring && !maintenanceForm.periodicityValue)} className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-50">
                <FaCheckCircle />
                {savingMaintenance ? 'Salvando...' : maintenanceForm.id ? 'Salvar alterações' : 'Registrar manutenção'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} size="max-w-xl" showCloseButton={false}>
        <div className="overflow-hidden rounded-2xl bg-slate-50">
          <div className="flex items-center justify-between bg-gradient-to-r from-[#071f54] to-[#0d4d91] px-5 py-4 text-white">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15"><FaLayerGroup /></span>
              <h2 className="text-lg font-semibold">{categoryForm.id ? 'Editar categoria' : 'Nova categoria'}</h2>
            </div>
            <button type="button" onClick={() => setCategoryModalOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20"><FaTimes /></button>
          </div>
          <div className="space-y-4 p-5">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              <span className="inline-flex items-center gap-1">Nome <span className="text-rose-500">*</span></span>
              <input type="text" value={categoryForm.name} onChange={(event) => setCategoryForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Ex.: Equipamentos de informática" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              Descrição
              <textarea rows={3} value={categoryForm.description} onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
            </label>
            <label className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-3">
              <span className="text-sm font-medium text-slate-700">Cor identificadora</span>
              <span className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg border border-slate-200" style={{ backgroundColor: categoryForm.color }} />
                <input type="color" value={categoryForm.color} onChange={(event) => setCategoryForm((prev) => ({ ...prev, color: event.target.value }))} className="h-9 w-12 cursor-pointer rounded-lg border border-slate-200 bg-white p-1" />
              </span>
            </label>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <button type="button" onClick={() => setCategoryModalOpen(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button type="button" onClick={handleSaveCategory} disabled={savingCategory || !String(categoryForm.name||'').trim()} className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0a2c62] disabled:opacity-50"><FaCheckCircle />{savingCategory ? 'Salvando...' : 'Salvar categoria'}</button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={locationModalOpen} onClose={() => setLocationModalOpen(false)} size="max-w-xl" showCloseButton={false}>
        <div className="overflow-hidden rounded-2xl bg-slate-50">
          <div className="flex items-center justify-between bg-gradient-to-r from-[#071f54] to-[#0d4d91] px-5 py-4 text-white">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15"><FaMapMarkedAlt /></span>
              <h2 className="text-lg font-semibold">{locationForm.id ? 'Editar local' : 'Novo local'}</h2>
            </div>
            <button type="button" onClick={() => setLocationModalOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20"><FaTimes /></button>
          </div>
          <div className="space-y-4 p-5">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              <span className="inline-flex items-center gap-1">Nome <span className="text-rose-500">*</span></span>
              <input type="text" value={locationForm.name} onChange={(event) => setLocationForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Ex.: Almoxarifado, Sala 12, Filial..." className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              Descrição
              <textarea rows={3} value={locationForm.description} onChange={(event) => setLocationForm((prev) => ({ ...prev, description: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                Responsável
                <input type="text" value={locationForm.responsible} onChange={(event) => setLocationForm((prev) => ({ ...prev, responsible: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
              </label>
              <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                Contato
                <input type="text" value={locationForm.contact} onChange={(event) => setLocationForm((prev) => ({ ...prev, contact: event.target.value }))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <button type="button" onClick={() => setLocationModalOpen(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button type="button" onClick={handleSaveLocation} disabled={savingLocation || !String(locationForm.name||'').trim()} className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0a2c62] disabled:opacity-50"><FaCheckCircle />{savingLocation ? 'Salvando...' : 'Salvar local'}</button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
